import Dexie, { type EntityTable, type Table, type Transaction } from 'dexie';
import type { DashboardSummary } from '@/services/dashboard.service';
import { hasImageBytes, blobToArrayBuffer } from './photo-blob';
import type { FieldBox } from './cardAnchor';
import type { VoiceNoteRecordedIn } from '@/types/domain.types';
import { setDbVersionErrorOverride } from './pwa-update-flag';
import { FIELD_BLANK_SOURCE, BLANK_CARD_SHA256, blankCardPdfBytes } from './field-blank-card';

export type ParcelKey = [municipalityId: string, cycleId: string, roll: string];

/**
 * Sentinel planId for parcels NOT in any inspection plan.
 * These are "cycle background" parcels downloaded for ad-hoc inspection.
 */
export const UNPLANNED_PLAN_ID = '__cycle__';
export const ONLINE_PLAN_ID = 'online';

/**
 * Parcel data for field inspection
 */
export interface Parcel {
  id: string;                    // roll_number (unique per municipality+cycle; PK is [municipalityId+cycleId+id])
  propertyId?: string;           // Firestore document ID (used for buildings API)
  municipalityId: string;
  cycleId: string;               // Assessment cycle this parcel belongs to
  planId: string;                // Which inspection plan this parcel belongs to
  address: string;
  owner?: string;
  // FI#5 camera-overlay sources (property-level, joined from CAMA at export).
  propertyDescription?: string | null;
  ruralLegal?: string | null;
  urbanLegal?: string | null;
  // Legacy single outer ring kept so rows synced before the MultiPolygon
  // fix render via the old single-ring code path. For multi-part parcels
  // this is the largest part by vertex count.
  boundary: [number, number][];
  // Full list of outer rings, one per disjoint MultiPolygon part. Renderers
  // prefer this when non-empty so every piece of a multi-part parcel is
  // drawn. Optional because rows synced from the prior backend bundle do
  // not have it; rendering then falls back to `boundary`.
  boundaryRings?: [number, number][][];
  // Parts-with-holes: one entry per disjoint part, each [outer, hole, ...].
  // Preferred for rendering so donut parcels show transparent cut-outs and
  // parcels inside the cut-outs stay clickable. Optional because rows synced
  // before the holes fix lack it; rendering then falls back to boundaryRings.
  boundaryParts?: [number, number][][][];
  center: [number, number];
  inspected: boolean;            // true = has photos (local or synced), false = not done
  syncedPhotoCount: number;      // Number of photos synced to server (persists after local clear)
  // Server-seeded durable photo-presence signal (F2 photo-half fix, Kurt
  // 2026-08-21 — "If there are no notes or pictures on a property, then it
  // should not be highlighted as complete. Even if there was a note and
  // then we deleted it, it should still not show as complete," which
  // covers photos exactly as much as notes). Analog of `serverHasNotes`,
  // NOT of `syncedPhotoCount` above: `syncedPhotoCount` is a monotonic
  // local high-water mark nothing ever decrements (comment above), while
  // `serverPhotoCount` is the server's ROUND-CUTOFF-FILTERED, live
  // `photo_count` (backend/routes/parcels.py ~113-122, ~526-530) adopted
  // VERBATIM on every merge — deletion-aware, so a re-download can bring
  // it back down to 0. UNINDEXED (never queried by index) — NO Dexie
  // version bump, same precedent as `serverHasNotes` immediately below.
  // Optional and ABSENT (not 0) until this row is re-downloaded on a
  // post-upgrade sync: the merge sites (parcel-sync.service.ts) and
  // computeInspectedSweep (round-sweep.ts) MUST treat absent and 0 as
  // distinct — a device whose row hasn't been re-downloaded yet must keep
  // reading legacy `syncedPhotoCount` for its inspected status, never
  // collapse straight to "0 photos".
  serverPhotoCount?: number;
  // 4-state map status (notes signal, fieldmap 4-state spec §4.1). Both
  // UNINDEXED — booleans are not valid IndexedDB keys and nothing queries
  // them by index; adding unindexed fields to existing rows needs NO Dexie
  // version bump (CardMarkRow 2b/2c precedent below). Optional: rows synced
  // before this feature lack them; readers must Boolean() them.
  hasNotes?: boolean;        // merged flag, analog of `inspected`
  serverHasNotes?: boolean;  // server-seeded durable signal, analog of syncedPhotoCount
  // Feature 7 (inspection types + rounds). Both UNINDEXED — arrays/numbers
  // never queried by index, so NO Dexie version bump (CardMarkRow 2b/2c
  // precedent). Optional: rows synced before this feature lack them.
  inspectionTypes?: string[];   // server inspection_types — drives field-map bucket rendering (parcel-bucket.ts)
  roundStartedAt?: number;      // effective cutoff: EARLIEST applicable round start (ms); 0/absent = no cutoff
  syncedAt: number;
}

/**
 * Inspection plan for offline field work
 */
export interface InspectionPlan {
  id: string;                    // Plan ID from backend
  municipalityId: string;
  cycleId: string;
  parcelIds: string[];           // Roll numbers to inspect
  parcelCount: number;
  name?: string;
  status: 'draft' | 'active' | 'completed' | 'archived';
  syncedAt: number;              // When parcels were downloaded
  parcelsDownloaded: boolean;    // Whether parcel data is cached
}

/**
 * Photo captured during field inspection
 */
export interface Photo {
  id: string;
  municipalityId: string;
  cycleId: string;
  parcelId: string;  // roll_number from submissions
  buildingId?: string; // Optional link to a building for image checklist
  // Image bytes stored as an ArrayBuffer, NOT a Blob: iOS/WebKit corrupts and
  // empties Blobs persisted in IndexedDB (data lost after navigation / eviction;
  // see photo-blob.ts). Always read via getPhotoBlob(). `blob` is legacy — only
  // present on records written before this migration; kept for back-compat reads.
  bytes?: ArrayBuffer;
  mimeType?: string;
  blob?: Blob;
  timestamp: number;
  synced: boolean;
  // Indexable mirror of `synced` (0 = unsynced, 1 = synced). IndexedDB cannot
  // index booleans, so `synced` is unqueryable by index and counting/listing
  // unsynced photos otherwise requires a `.filter()` that deserializes every
  // record — including the multi-MB `bytes` payload — on the hot path (after
  // every capture and on each ~10s auto-sync tick). syncedFlag is indexed
  // ([municipalityId+cycleId+syncedFlag] and standalone) so those queries are
  // pure index reads that never touch image bytes. MUST be kept in lockstep
  // with `synced` at every write site (savePhoto / markPhotoSynced /
  // markPhotosSynced); backfilled for legacy rows by the v9 migration.
  syncedFlag?: 0 | 1;
  syncedAt?: number;
  latitude?: number;
  longitude?: number;
  heading?: number;  // compass heading in degrees (0-360)
}

/**
 * Cached municipality data for offline use
 */
export interface CachedMunicipality {
  id: string;
  name: string;
  cachedAt: number;
}

/**
 * Cached cycle data for offline use
 */
export interface CachedCycle {
  id: string;
  municipalityId: string;
  name: string;
  year: number;
  status: string;
  cachedAt: number;
}

/**
 * Voice note recorded during field inspection
 */
export interface VoiceNote {
  id: string;
  municipalityId: string;
  cycleId: string;
  propertyId: string;
  buildingId?: string;
  // Audio bytes stored as an ArrayBuffer, NOT a Blob: iOS/WebKit empties Blobs
  // persisted in IndexedDB after navigation/eviction (the bytes read back as 0),
  // so a pending voice note uploads as a 0-byte file the backend rejects with a
  // 400 — which voice-sync never retries, wedging the note forever. Always read
  // via getVoiceBlob(). `blob` is legacy — only present on records written before
  // this migration; kept for back-compat reads: getVoiceBlob falls back to it,
  // recovering a pre-migration note as long as iOS hasn't yet emptied its bytes.
  // See photo-blob.ts.
  bytes?: ArrayBuffer;
  blob?: Blob;
  durationSeconds: number;
  mimeType: string;
  transcription?: string;
  synced: boolean;
  syncedAt?: number;
  timestamp: number;
  // Quarantine flag (Kurt item 8 residual, spec §C4): set to 1 once voice-sync
  // determines this note can NEVER succeed — its audio bytes are permanently
  // gone (iOS Blob purge; getVoiceBlob returns null) or the server permanently
  // 400-rejected the upload (malformed WebM; a definitively non-retryable
  // client error, never a transient 5xx/network failure). Indexable numeric
  // mirror, same idiom as photos' syncedFlag (db.ts:104) — IndexedDB cannot
  // index booleans, and `parcelHasLocalNotes`/`getLocalNoteSignals` must stay
  // index-only (voiceNote rows embed multi-MB audio ArrayBuffers that must
  // never be deserialized just to count/zip them). Optional and 0|1 like
  // syncedFlag: existing rows simply lack it, which is the correct default
  // (undefined ⇒ not dead ⇒ still counts) — no upgrade/backfill needed (v6
  // buildingId-on-photos precedent). Two healing paths: (1) a later
  // successful re-record creates a brand-new row with no flag at all, so
  // Notes status heals via the existing recompute/sweep with no extra code;
  // (2) markVoiceNoteSynced clears deadFlag unconditionally on ANY sync
  // confirmation — covers the race where THIS row was dead-flagged (missing
  // local bytes) after it had already reached the server (app killed before
  // the local synced write, or reconcile-sync's cross-check confirming it
  // server-side later) — server confirmation is definitive proof the note
  // isn't dead.
  deadFlag?: 0 | 1;
  // Where in the field app the note was recorded. Rows written before this field lack it at runtime
  // and are healed by healVoiceNoteRecordedIn when uploaded.
  recordedIn: VoiceNoteRecordedIn;
}

/**
 * Building associated with a property for image checklist tracking
 */
export interface Building {
  id: string;                    // UUID
  municipalityId: string;
  cycleId: string;
  propertyId: string;            // Firestore document UUID (NOT roll_number)
  buildingType: string;          // "residential", "garage", "shed", "barn", "commercial", "other"
  label: string;
  source: 'imported' | 'field_discovered';
  expected: boolean;
  photoCount: number;
  status: 'pending' | 'documented' | 'skipped';
  notes?: string;
  sortOrder: number;
  createdBy?: string;
  syncedAt: number;
  camaDescription?: string | null;  // Raw CAMA IMPROVEMENTHEADER.DESCRIPTION; preferred label
  modelQk?: number | null;
  qualityQk?: number | null;
  structureQk?: number | null;
  yearBuilt?: number | null;
  mainImprovement?: boolean | null;  // CAMA MAINIMPROVEMENT flag; main improvement sorts first
  camaStatus?: string | null;  // "not_in_cama": dropped from the latest CAMA import but kept (has field data)
}

/**
 * App settings
 */
export interface Setting {
  key: string;
  value: string | number | boolean | Record<string, unknown>;
}

/**
 * Offline display layer for a municipality (inspection 1b-1, spec §3)
 */
export interface MapLayerRow {
  id: string;                    // `${municipalityId}_${layerKey}` — mirrors backend MAP_LAYERS doc id
  municipalityId: string;
  layerKey: string;
  name: string;
  geometryType: string;
  style: Record<string, unknown>;
  visibleDefault: boolean;
  propertyKeys: string[];
  status: string;
  version: number;
  sourceGeneration: number | null;
  fileBytes: number;
  geojson: string;               // raw FeatureCollection text — string, never Blob (iOS purges IDB Blobs)
  downloadedAt: number;
}

/**
 * Resumable download-bundle progress record (inspection 1b-1, spec §7)
 */
export interface BundleManifest {
  id: string;                    // `${municipalityId}_${planScope}_${bundle}`
  municipalityId: string;
  planScope: string;             // plan id, or UNPLANNED_PLAN_ID for muni-scoped bundles (spec §7)
  bundle: 'vectors' | 'ortho' | 'basemap' | 'pdfs';
  version: string;               // vectors: sha-256 of sorted (layerKey, version) pairs
  expectedCount: number;
  expectedBytes: number;
  writtenCount: number;
  writtenBytes: number;
  nextChunkIndex: number;        // resume cursor — chunk commits are strictly sequential (spec §7)
  completed: boolean;            // written only after the last chunk
  updatedAt: number;
  /**
   * Ortho bundles only — optional so the other bundle kinds are untouched,
   * and NO Dexie version bump (neither field is indexed). Which archive the
   * finalized OPFS file holds ('standard' = offline.pmtiles, 'full' =
   * full.pmtiles) and the max zoom natively present in it. A pre-feature
   * manifest has neither: readers treat it as 'standard' with
   * nativeMaxZoom = registry.offlineMaxZoom (offline-depth spec §2).
   */
  depth?: 'standard' | 'full';
  nativeMaxZoom?: number;
}

/**
 * Record-card (record-card PDF) metadata row (inspection 1d-1, spec §5).
 *
 * Split from RecordCardBlobRow so delta sync can diff versions by reading
 * METADATA ONLY: a single table would materialize ~600MB of blob bytes just
 * to compare versions, and Dexie cannot project a subset of fields off a row.
 */
export interface RecordCardMetaRow {
  id: string;                    // `${municipalityId}_${roll}` — single active cycle per muni on device
  municipalityId: string;
  cycleId: string;
  roll: string;
  propertyId?: string;           // Firestore property doc id from the registry (2b voice capture)
  version: number;
  fileBytes: number;
  pageCount: number;
  sha256: string;
  downloadedAt: number;
  // Cardless field sketch (Wave G Task B, spec 2026-07-23). Both UNINDEXED —
  // plain optional fields on an existing row need NO Dexie version bump
  // (CardMarkRow 2b/2c precedent above); neither is ever queried by index.
  // `source` mirrors the registry doc's own field (`FIELD_BLANK_SOURCE` for a
  // synthetic blank; absent/undefined for every real card — including every
  // row synced before this feature). A card reached via the normal
  // registry-authenticated delta-sync path (record-card-sync.service.ts) also
  // carries this, echoed from the server.
  source?: string;
  // Local-only registration bookkeeping for a `source === FIELD_BLANK_SOURCE`
  // row — meaningless (and always left absent) for a real card:
  //   0 (or absent) — not yet confirmed registered server-side; retried by
  //     syncCardsForMuni's registration pass each sync, and EXEMPT from
  //     deleteRecordCardsNotIn's prune (see that function's doc comment).
  //   1 — the mint endpoint confirmed the server knows this roll's blank
  //     (fresh mint or `alreadyExists` with `source === FIELD_BLANK_SOURCE`).
  //     The server now enumerates it, so normal prune rules apply from here.
  //   2 — TERMINAL registration failure, from EITHER of two server verdicts
  //     (registerFieldBlankCard documents both): a mint 400 — a roll the
  //     backend's GCS-path sanitizer refuses, e.g. a '..' traversal sequence;
  //     ordinary dotted rolls like High Level's "0888.500" now mint fine — or
  //     an `alreadyExists` REAL card that has no servable version (doc-level
  //     too_large). Never retried again, but does NOT claim server presence —
  //     stays prune-EXEMPT forever (it exists only on this device).
  //     "Never" is scoped to the server rule in force when it was recorded.
  //     v14 resets EVERY field-blank `2` (both producers) back to 0 exactly
  //     once, because a stored verdict cannot outlive the rule behind it; a
  //     row that is still terminal re-derives `2` on the next sync.
  registered?: 0 | 1 | 2;
  // Durable Photos-chip evidence for the RollSearchSheet unmapped-cards list
  // (B2 review finding [1]). A MAPPED roll's synced-photo credit survives a
  // sync-time clear via Parcel.syncedPhotoCount — but an unmapped roll has NO
  // parcel row to carry that credit, and clearSyncedPhotos (photo-sync +
  // reconcile-sync) then bulk-deletes the now-synced `photos` row, so
  // getRollsWithLocalPhotos (a LIVE scan of that table) sees nothing —
  // silently erasing the Photos chip for a roll that WAS photographed and
  // successfully uploaded. Stamped `1` by markPhotoSyncedAndBumpParcel
  // UNCONDITIONALLY whenever a photo transitions to synced (round-2 fix,
  // finding [1]: the common real ordering credits the parcel FIRST — the
  // parcel row still exists when the photo syncs — and only LATER does the
  // parcel row itself get deleted, e.g. clearParcelsForPlan in
  // parcel-sync.service.ts or deleteParcelsForCycle in FieldInspectionTab;
  // stamping only on the parcel-missing branch covered just the rarer
  // opposite ordering). `.update()` against a missing meta id is a silent
  // no-op (Dexie contract): a roll with neither a parcel nor a downloaded
  // card never appears in the RollSearchSheet unmapped-cards list this flag
  // feeds, so there is nothing to stamp and nothing user-visible is lost
  // either way. Read by unmappedCardRows (roll-search-results.ts) as an OR
  // alongside live photoRolls (getRollsWithLocalPhotos) membership.
  //
  // NOT actually "never cleared" by Dexie on its own: a card re-download or
  // version bump bulkPuts a FRESH meta object built from the server
  // payload/echo (record-card-sync.service.ts downloadChunkOnce) that never
  // sets this device-local-only field, and bulkPut is whole-row `put`
  // semantics — a naive bulkPut would silently wipe an already-stamped
  // marker (B2 review finding [2]). saveRecordCards and
  // replaceRecordCardsWithMarkDrop guard against that by running
  // carryOverSyncedPhotoEvidence (db.ts, defined next to those two
  // functions) INSIDE the same transaction as their bulkPut, merging each
  // existing local row's `syncedPhotoEvidence` onto its incoming replacement
  // before the write commits — the same durability contract `source`/
  // `registered` get, made explicit here because (unlike those two) there is
  // no server-side value this field can be re-derived from.
  //
  // That carry-over is itself cycle-scoped (round-3 B2 re-review, finding
  // [2b]): it only fires when the existing row's cycleId matches the
  // incoming meta's cycleId. A same-cycle re-download/version bump keeps the
  // flag (the case above); a card re-download that lands in a NEW cycle
  // drops it, matching every other evidence signal here, which is
  // [muni+cycle]-scoped — see carryOverSyncedPhotoEvidence's doc comment.
  // Optional/UNINDEXED, same no-Dexie-bump rule as `source`/`registered`
  // above: never queried by index.
  syncedPhotoEvidence?: 1;
}

/**
 * Record-card PDF bytes, stored separately from its metadata row (see
 * RecordCardMetaRow doc comment). Bytes are stored as an ArrayBuffer, NEVER a
 * Blob — iOS/WebKit purges Blobs from IndexedDB (same fix as photos/voiceNotes).
 */
export interface RecordCardBlobRow {
  id: string;                    // same key as the meta row
  municipalityId: string;
  bytes: ArrayBuffer;
}

/**
 * Value-stripped field-box manifest for a record card, cached offline for
 * capture-time field anchoring (inspection 2c, cardAnchor.ts). Kept in a
 * SEPARATE store from RecordCardMetaRow rather than as extra props on the
 * meta row: getRecordCardMetasForMuni materializes EVERY meta row into
 * memory on every delta sync (record-card-sync.service.ts), so a ~10-30KB
 * manifest per card on the meta row would bloat that scan to tens of MB for
 * a large muni — the same OOM class the meta/blob split was built to avoid.
 * A separate store keeps the meta scan tiny; getCardManifest reads ONE row
 * by id.
 */
export interface RecordCardManifestRow {
  id: string;                    // `${municipalityId}_${roll}` — same key as the meta/blob rows
  municipalityId: string;
  cycleId: string;
  // A FieldBox[] for a card that HAS a manifest, or `null` for a NEGATIVE row
  // — recorded when the server offers no manifest URL for the roll at all, so
  // the delta/repair sweep stops re-fetching a manifest that will never exist
  // and getCardManifest naturally reads it back as "no manifest" (inspection
  // 2c self-heal, finding [34]). A transient fetch failure writes NO row at
  // all (the sweep retries that). Not a Dexie index → no schema/version bump.
  manifest: FieldBox[] | null;
  manifestVersion: number;        // == the card's meta version at fetch time
}

/**
 * A pencil/voice/arrow annotation on a record-card page (inspection 2a,
 * spec §5). LOCAL-ONLY in 2a: rows never leave the device (sync/review is
 * 2b — `synced` stays false, syncedFlag 0). Geometry is normalized 0–1
 * page-fraction, top-left origin (see lib/ink-engine.ts), EXCEPT
 * noteStrokes which are in the fixed-size note canvas's own CSS px.
 *
 * cycleId is IN the key material — recordCards' `${muni}_${roll}`
 * single-active-cycle shape is deliberately NOT copied for user-created
 * data. Rows are KB-scale JSON — no ArrayBuffer/Blob concerns.
 */
export interface CardMarkRow {
  id: string;                     // crypto.randomUUID() at creation — stable across 2b sync (photos precedent)
  municipalityId: string;
  cycleId: string;
  roll: string;                   // parcel id / exact-string roll, matches recordCards + RecordCardSheet props
  propertyId?: string;            // Firestore property doc id when the parcel carries one
  page: number;                   // 1-based, matches viewer pageNum / pageRefs keys
  kind: 'ink' | 'voice' | 'arrow' | 'location';
  stroke?: { points: Array<{ x: number; y: number }> };  // ink polyline, normalized
  point?: { x: number; y: number };   // voice-dot position / arrow TIP
  tail?: { x: number; y: number };    // arrow tail / location note-bubble anchor
  latitude?: number;              // location WGS84 lat, captured precision — location kind only
  longitude?: number;             // location WGS84 lng, captured precision — location kind only
  note?: string;                  // unused in 2a (no typed input); forward-compat with card_annotations.note_text
  noteStrokes?: Array<Array<{ x: number; y: number }>>;  // note-canvas-LOCAL CSS px
  cardVersion: number;            // recordCards meta version at creation (drift review is 2b's job)
  createdAt: number;
  updatedAt: number;
  createdBy?: string;             // Firebase uid from the existing auth context
  synced: boolean;                // always false in 2a
  syncedFlag: 0 | 1;              // indexed numeric mirror from day one (photos-v9 lesson)
  syncedAt?: number;
  // 2b sync fields — all unindexed, so the schema stays v12 (no migration).
  deleted?: boolean;              // tombstone: erased AFTER a successful sync; propagates the delete
  deletedAt?: number;
  voiceNoteId?: string;           // voiceNotes FK for voice dots (2b audio capture)
  uploadedAt?: number;            // stamped whenever the server is known to have accepted THIS id —
                                   // including a CAS-skip replay — so a later erase tombstones
                                   // instead of hard-deleting a row the server has a copy of.
  // 2c field anchoring — unindexed, no Dexie bump
  resolvedField?: string;         // CAMA field key the mark resolved to
  fieldLabel?: string;            // human label for the resolved field
  fieldConfidence?: number;       // 0..1 anchor confidence
  fieldConfirmed?: boolean;       // true once the inspector or office confirms
  // note-bubble resize — user-set bubble size in bubble-local CSS px; absent ⇒ 210×52
  bubbleW?: number;
  bubbleH?: number;
  // per-stroke pen style (ink only) — unindexed; absent ⇒ navy 2.5
  inkColor?: string;
  inkWidth?: number;
}

/**
 * Custom error types
 */
export class StorageQuotaError extends Error {
  constructor(message = 'Storage quota exceeded') {
    super(message);
    this.name = 'StorageQuotaError';
  }
}

export class PhotoNotFoundError extends Error {
  constructor(photoId: string) {
    super(`Photo not found: ${photoId}`);
    this.name = 'PhotoNotFoundError';
  }
}

export class VoiceNoteNotFoundError extends Error {
  constructor(noteId: string) {
    super(`Voice note not found: ${noteId}`);
    this.name = 'VoiceNoteNotFoundError';
  }
}

export class DatabaseError extends Error {
  constructor(message: string, public cause?: unknown) {
    super(message);
    this.name = 'DatabaseError';
  }
}

/**
 * Field inspection database
 *
 * Schema versioning:
 * - v1: Initial schema with parcels, photos, settings tables
 * - v2: Added cycleId to photos for assessment cycle context
 * - v3: Added inspectionPlans table, updated parcels with cycleId/planId/visitStatus
 * - v4: Simplified parcel status - replaced visitStatus with inspected boolean + syncedPhotoCount
 * - v5: Added municipalities and cycles tables for offline caching
 * - v6: Added buildings table for building-level image checklist, added buildingId index to photos
 * - v7: Added voiceNotes table for field voice recordings with offline-first sync
 * - v8: Added latitude, longitude, heading fields to photos (optional, no new indexes)
 * - v9: Added syncedFlag (indexed numeric mirror of `synced`) to photos
 * - v10: mapLayers + bundleManifests (1b-1 offline vectors)
 * - v11: recordCards meta + recordCardBlobs (1d-1 offline record cards)
 * - v12: cardMarks (2a pencil annotations on record cards, local-only)
 * - v13: recordCardManifests (2c field-anchoring manifest cache, separate
 *   store so the delta-sync meta scan stays small)
 * - (no bump) Wave G Task B: RecordCardMetaRow gained optional `source` /
 *   `registered` fields (cardless field sketch) — plain optional fields on
 *   an existing store, neither indexed, so schema stays v13 (same rule as
 *   Parcel's hasNotes/serverHasNotes and CardMarkRow's 2b/2c fields above).
 * - v14: no schema change — data heal clearing field-blank `registered: 2`
 *   rows recorded under the pre-fix GCS-path sanitizer (dotted rolls now mint)
 * - (no bump) B2 review finding [1]: RecordCardMetaRow gained optional
 *   `syncedPhotoEvidence` (durable Photos-chip evidence for an unmapped
 *   roll's photo sync) — plain optional field on an existing store, never
 *   indexed, same no-bump rule as `source`/`registered` above.
 * - v16: parcels → fieldParcels, PK [municipalityId+cycleId+id] (rolls collide across munis)
 */
// Exported so db-migration-v16.test.ts runs the real body.
export async function upgradeParcelsToCompoundKey(tx: Transaction): Promise<void> {
  const source = tx.table<Parcel, string>('parcels');
  const target = tx.table<Parcel, ParcelKey>('fieldParcels');
  let after: string | undefined;
  for (;;) {
    const rows = await (after === undefined
      ? source.orderBy(':id')
      : source.where(':id').above(after)).limit(500).toArray();
    if (rows.length === 0) break;
    await target.bulkAdd(rows);
    after = rows[rows.length - 1].id;
  }
}

class FieldInspectionDB extends Dexie {
  fieldParcels!: Table<Parcel, ParcelKey>;
  photos!: EntityTable<Photo, 'id'>;
  inspectionPlans!: EntityTable<InspectionPlan, 'id'>;
  municipalities!: EntityTable<CachedMunicipality, 'id'>;
  cycles!: EntityTable<CachedCycle, 'id'>;
  buildings!: EntityTable<Building, 'id'>;
  voiceNotes!: EntityTable<VoiceNote, 'id'>;
  settings!: EntityTable<Setting, 'key'>;
  mapLayers!: EntityTable<MapLayerRow, 'id'>;
  bundleManifests!: EntityTable<BundleManifest, 'id'>;
  recordCards!: EntityTable<RecordCardMetaRow, 'id'>;
  recordCardBlobs!: EntityTable<RecordCardBlobRow, 'id'>;
  recordCardManifests!: EntityTable<RecordCardManifestRow, 'id'>;
  cardMarks!: EntityTable<CardMarkRow, 'id'>;

  constructor() {
    super('SmartExtractField');

    // v1: Initial schema (keep for migration path)
    this.version(1).stores({
      parcels: 'id, municipalityId, address',
      photos: 'id, municipalityId, parcelId, timestamp, synced',
      settings: 'key'
    });

    // v2: Added cycleId to photos for assessment cycle context
    this.version(2).stores({
      parcels: 'id, municipalityId, address',
      photos: 'id, municipalityId, cycleId, parcelId, [municipalityId+cycleId], [municipalityId+cycleId+parcelId], timestamp, synced',
      settings: 'key'
    }).upgrade(tx => {
      // Delete old photos without cycleId - they can't be migrated
      return tx.table('photos').toCollection().delete();
    });

    // v3: Added inspectionPlans table, updated parcels with cycleId/planId/visitStatus
    this.version(3).stores({
      parcels: 'id, municipalityId, cycleId, planId, [municipalityId+cycleId], [municipalityId+cycleId+planId], visitStatus',
      photos: 'id, municipalityId, cycleId, parcelId, [municipalityId+cycleId], [municipalityId+cycleId+parcelId], timestamp, synced',
      inspectionPlans: 'id, municipalityId, cycleId, [municipalityId+cycleId], status',
      settings: 'key'
    }).upgrade(tx => {
      // Delete old parcels - they don't have required cycleId/planId fields
      return tx.table('parcels').toCollection().delete();
    });

    // v4: Simplified parcel status - replaced visitStatus with inspected boolean + syncedPhotoCount
    this.version(4).stores({
      parcels: 'id, municipalityId, cycleId, planId, [municipalityId+cycleId], [municipalityId+cycleId+planId], inspected',
      photos: 'id, municipalityId, cycleId, parcelId, [municipalityId+cycleId], [municipalityId+cycleId+parcelId], timestamp, synced',
      inspectionPlans: 'id, municipalityId, cycleId, [municipalityId+cycleId], status',
      settings: 'key'
    }).upgrade(tx => {
      // Migrate visitStatus to inspected boolean
      return tx.table('parcels').toCollection().modify(parcel => {
        const oldStatus = (parcel as { visitStatus?: string }).visitStatus;
        parcel.inspected = oldStatus === 'has_photos' || oldStatus === 'visited';
        parcel.syncedPhotoCount = 0; // Reset - will be populated on next sync
        delete (parcel as { visitStatus?: string }).visitStatus;
      });
    });

    // v5: Added municipalities and cycles tables for offline caching
    this.version(5).stores({
      parcels: 'id, municipalityId, cycleId, planId, [municipalityId+cycleId], [municipalityId+cycleId+planId], inspected',
      photos: 'id, municipalityId, cycleId, parcelId, [municipalityId+cycleId], [municipalityId+cycleId+parcelId], timestamp, synced',
      inspectionPlans: 'id, municipalityId, cycleId, [municipalityId+cycleId], status',
      municipalities: 'id',
      cycles: 'id, municipalityId',
      settings: 'key'
    });

    // v6: Added buildings table for building-level image checklist, added buildingId index to photos
    this.version(6).stores({
      parcels: 'id, municipalityId, cycleId, planId, [municipalityId+cycleId], [municipalityId+cycleId+planId], inspected',
      photos: 'id, municipalityId, cycleId, parcelId, buildingId, [municipalityId+cycleId], [municipalityId+cycleId+parcelId], timestamp, synced',
      inspectionPlans: 'id, municipalityId, cycleId, [municipalityId+cycleId], status',
      municipalities: 'id',
      cycles: 'id, municipalityId',
      buildings: 'id, municipalityId, cycleId, propertyId, [municipalityId+cycleId], [municipalityId+cycleId+propertyId], status, syncedAt',
      settings: 'key'
    }).upgrade(_tx => {
      // No data migration needed for new buildings table
      // Photos gain optional buildingId index — existing photos just won't have it
      return Promise.resolve();
    });

    // v7: Added voiceNotes table for field voice recordings with offline-first sync
    this.version(7).stores({
      parcels: 'id, municipalityId, cycleId, planId, [municipalityId+cycleId], [municipalityId+cycleId+planId], inspected',
      photos: 'id, municipalityId, cycleId, parcelId, buildingId, [municipalityId+cycleId], [municipalityId+cycleId+parcelId], timestamp, synced',
      inspectionPlans: 'id, municipalityId, cycleId, [municipalityId+cycleId], status',
      municipalities: 'id',
      cycles: 'id, municipalityId',
      buildings: 'id, municipalityId, cycleId, propertyId, [municipalityId+cycleId], [municipalityId+cycleId+propertyId], status, syncedAt',
      voiceNotes: 'id, municipalityId, cycleId, propertyId, buildingId, [municipalityId+cycleId], [municipalityId+cycleId+propertyId], synced, timestamp',
      settings: 'key'
    }).upgrade(_tx => {
      // No data migration needed for new voiceNotes table
      return Promise.resolve();
    });

    // v8: Added latitude, longitude, heading fields to photos (optional, no new indexes)
    this.version(8).stores({
      parcels: 'id, municipalityId, cycleId, planId, [municipalityId+cycleId], [municipalityId+cycleId+planId], inspected',
      photos: 'id, municipalityId, cycleId, parcelId, buildingId, [municipalityId+cycleId], [municipalityId+cycleId+parcelId], timestamp, synced',
      inspectionPlans: 'id, municipalityId, cycleId, [municipalityId+cycleId], status',
      municipalities: 'id',
      cycles: 'id, municipalityId',
      buildings: 'id, municipalityId, cycleId, propertyId, [municipalityId+cycleId], [municipalityId+cycleId+propertyId], status, syncedAt',
      voiceNotes: 'id, municipalityId, cycleId, propertyId, buildingId, [municipalityId+cycleId], [municipalityId+cycleId+propertyId], synced, timestamp',
      settings: 'key'
    });

    this.version(9).stores({
      parcels: 'id, municipalityId, cycleId, planId, [municipalityId+cycleId], [municipalityId+cycleId+planId], inspected',
      // syncedFlag (numeric 0/1 mirror of `synced`) added as a standalone index
      // and a [municipalityId+cycleId+syncedFlag] compound index so the
      // pending-count and unsynced-list queries become pure index reads.
      // Booleans aren't valid IndexedDB keys, so the old `synced` index was dead
      // and those queries fell back to a full-record `.filter()` that
      // deserialized every photo's multi-MB `bytes` payload on the hot path.
      // `synced` (boolean) is intentionally NOT indexed — IndexedDB can't index
      // booleans, so the prior `synced` index was always dead. It stays a stored
      // field (read as `photo.synced` throughout); syncedFlag is the indexed view.
      photos: 'id, municipalityId, cycleId, parcelId, buildingId, [municipalityId+cycleId], [municipalityId+cycleId+parcelId], [municipalityId+cycleId+syncedFlag], timestamp, syncedFlag',
      inspectionPlans: 'id, municipalityId, cycleId, [municipalityId+cycleId], status',
      municipalities: 'id',
      cycles: 'id, municipalityId',
      buildings: 'id, municipalityId, cycleId, propertyId, [municipalityId+cycleId], [municipalityId+cycleId+propertyId], status, syncedAt',
      voiceNotes: 'id, municipalityId, cycleId, propertyId, buildingId, [municipalityId+cycleId], [municipalityId+cycleId+propertyId], synced, timestamp',
      settings: 'key'
    }).upgrade(async (tx) => {
      // Backfill syncedFlag on photos written before v9 so they appear in the
      // new index. Without this, a legacy unsynced photo would be invisible to
      // the pending-count/unsynced queries and would never upload. One-time
      // cost on the first launch of this build (bounded by the pending backlog,
      // since synced photos are cleared after upload).
      await tx.table('photos').toCollection().modify((p: Photo) => {
        p.syncedFlag = p.synced ? 1 : 0;
      });
    });

    // v10: offline display layers + bundle manifests (inspection 1b-1, spec §3/§7).
    // Additive — no data transform needed.
    this.version(10).stores({
      parcels: 'id, municipalityId, cycleId, planId, [municipalityId+cycleId], [municipalityId+cycleId+planId], inspected',
      photos: 'id, municipalityId, cycleId, parcelId, buildingId, [municipalityId+cycleId], [municipalityId+cycleId+parcelId], [municipalityId+cycleId+syncedFlag], timestamp, syncedFlag',
      inspectionPlans: 'id, municipalityId, cycleId, [municipalityId+cycleId], status',
      municipalities: 'id',
      cycles: 'id, municipalityId',
      buildings: 'id, municipalityId, cycleId, propertyId, [municipalityId+cycleId], [municipalityId+cycleId+propertyId], status, syncedAt',
      voiceNotes: 'id, municipalityId, cycleId, propertyId, buildingId, [municipalityId+cycleId], [municipalityId+cycleId+propertyId], synced, timestamp',
      settings: 'key',
      mapLayers: 'id, municipalityId',
      bundleManifests: 'id, municipalityId',
    }).upgrade(_tx => {
      // New tables only; Dexie creates them from the schema diff.
      return Promise.resolve();
    });

    // v11: recordCards meta + recordCardBlobs (inspection 1d-1, spec §5).
    // Additive — no data transform needed. Meta and blob bytes are split
    // across two tables (see RecordCardMetaRow doc comment) so delta sync can
    // diff versions by reading metadata only, never materializing blob bytes.
    this.version(11).stores({
      parcels: 'id, municipalityId, cycleId, planId, [municipalityId+cycleId], [municipalityId+cycleId+planId], inspected',
      photos: 'id, municipalityId, cycleId, parcelId, buildingId, [municipalityId+cycleId], [municipalityId+cycleId+parcelId], [municipalityId+cycleId+syncedFlag], timestamp, syncedFlag',
      inspectionPlans: 'id, municipalityId, cycleId, [municipalityId+cycleId], status',
      municipalities: 'id',
      cycles: 'id, municipalityId',
      buildings: 'id, municipalityId, cycleId, propertyId, [municipalityId+cycleId], [municipalityId+cycleId+propertyId], status, syncedAt',
      voiceNotes: 'id, municipalityId, cycleId, propertyId, buildingId, [municipalityId+cycleId], [municipalityId+cycleId+propertyId], synced, timestamp',
      settings: 'key',
      mapLayers: 'id, municipalityId',
      bundleManifests: 'id, municipalityId',
      recordCards: 'id, municipalityId',
      recordCardBlobs: 'id, municipalityId',
    }).upgrade(_tx => {
      // New tables only; Dexie creates them from the schema diff.
      return Promise.resolve();
    });

    // v12: cardMarks (inspection 2a, spec §5) — local-only pencil annotations
    // on record cards. Additive — no data transform needed. Keyed by UUID with
    // cycleId IN the compound indexes (recordCards' `${muni}_${roll}`
    // single-cycle shape is deliberately NOT copied for user-created data),
    // and an indexed numeric syncedFlag mirror from day one (photos-v9
    // lesson: booleans are not valid IndexedDB keys).
    this.version(12).stores({
      parcels: 'id, municipalityId, cycleId, planId, [municipalityId+cycleId], [municipalityId+cycleId+planId], inspected',
      photos: 'id, municipalityId, cycleId, parcelId, buildingId, [municipalityId+cycleId], [municipalityId+cycleId+parcelId], [municipalityId+cycleId+syncedFlag], timestamp, syncedFlag',
      inspectionPlans: 'id, municipalityId, cycleId, [municipalityId+cycleId], status',
      municipalities: 'id',
      cycles: 'id, municipalityId',
      buildings: 'id, municipalityId, cycleId, propertyId, [municipalityId+cycleId], [municipalityId+cycleId+propertyId], status, syncedAt',
      voiceNotes: 'id, municipalityId, cycleId, propertyId, buildingId, [municipalityId+cycleId], [municipalityId+cycleId+propertyId], synced, timestamp',
      settings: 'key',
      mapLayers: 'id, municipalityId',
      bundleManifests: 'id, municipalityId',
      recordCards: 'id, municipalityId',
      recordCardBlobs: 'id, municipalityId',
      cardMarks: 'id, municipalityId, [municipalityId+cycleId+roll], [municipalityId+cycleId+syncedFlag]',
    }).upgrade(_tx => {
      // New table only; Dexie creates it from the schema diff.
      return Promise.resolve();
    });

    // v13: recordCardManifests (inspection 2c field anchoring). Separate
    // store (NOT on the meta row) so the delta-sync meta scan stays small.
    this.version(13).stores({
      parcels: 'id, municipalityId, cycleId, planId, [municipalityId+cycleId], [municipalityId+cycleId+planId], inspected',
      photos: 'id, municipalityId, cycleId, parcelId, buildingId, [municipalityId+cycleId], [municipalityId+cycleId+parcelId], [municipalityId+cycleId+syncedFlag], timestamp, syncedFlag',
      inspectionPlans: 'id, municipalityId, cycleId, [municipalityId+cycleId], status',
      municipalities: 'id',
      cycles: 'id, municipalityId',
      buildings: 'id, municipalityId, cycleId, propertyId, [municipalityId+cycleId], [municipalityId+cycleId+propertyId], status, syncedAt',
      voiceNotes: 'id, municipalityId, cycleId, propertyId, buildingId, [municipalityId+cycleId], [municipalityId+cycleId+propertyId], synced, timestamp',
      settings: 'key',
      mapLayers: 'id, municipalityId',
      bundleManifests: 'id, municipalityId',
      recordCards: 'id, municipalityId',
      recordCardBlobs: 'id, municipalityId',
      cardMarks: 'id, municipalityId, [municipalityId+cycleId+roll], [municipalityId+cycleId+syncedFlag]',
      recordCardManifests: 'id, municipalityId',
    }).upgrade(_tx => {
      // New table only; Dexie creates it from the schema diff.
      return Promise.resolve();
    });

    // v14: NO schema change — a data heal. `registered: 2` is recorded from a
    // SERVER verdict ("this roll can never mint"), so it is only as durable as
    // the server rule that produced it. The GCS-path sanitizer used to reject
    // every dotted roll (High Level's "0888.500"); it now accepts them and
    // rejects only genuine traversal. Rows marked terminal under the OLD rule
    // would otherwise stay terminal forever, since the retry pass skips `2`.
    // Resetting them to 0 re-asks the server exactly once: rolls that are
    // still refused (a real '..') are re-marked 2 on the next sync, and a doc
    // with no servable version re-resolves to 2 the same way — so this costs
    // one idempotent POST per genuinely-terminal row and heals the rest.
    this.version(14).stores({}).upgrade(tx => {
      return tx.table('recordCards')
        .toCollection()
        .modify(row => {
          if (row.source === FIELD_BLANK_SOURCE && row.registered === 2) {
            row.registered = 0;
          }
        });
    });

    // v15: deadFlag index on voiceNotes (Kurt item 8 residual, spec §C4) — see
    // the VoiceNote.deadFlag doc comment. Additive index only, same pattern as
    // v6's buildingId addition to photos: existing rows simply lack the field
    // and are absent from the deadFlag index, which is exactly the desired
    // default (not dead) — no backfill needed.
    this.version(15).stores({
      parcels: 'id, municipalityId, cycleId, planId, [municipalityId+cycleId], [municipalityId+cycleId+planId], inspected',
      photos: 'id, municipalityId, cycleId, parcelId, buildingId, [municipalityId+cycleId], [municipalityId+cycleId+parcelId], [municipalityId+cycleId+syncedFlag], timestamp, syncedFlag',
      inspectionPlans: 'id, municipalityId, cycleId, [municipalityId+cycleId], status',
      municipalities: 'id',
      cycles: 'id, municipalityId',
      buildings: 'id, municipalityId, cycleId, propertyId, [municipalityId+cycleId], [municipalityId+cycleId+propertyId], status, syncedAt',
      voiceNotes: 'id, municipalityId, cycleId, propertyId, buildingId, [municipalityId+cycleId], [municipalityId+cycleId+propertyId], synced, timestamp, deadFlag',
      settings: 'key',
      mapLayers: 'id, municipalityId',
      bundleManifests: 'id, municipalityId',
      recordCards: 'id, municipalityId',
      recordCardBlobs: 'id, municipalityId',
      cardMarks: 'id, municipalityId, [municipalityId+cycleId+roll], [municipalityId+cycleId+syncedFlag]',
      recordCardManifests: 'id, municipalityId',
    }).upgrade(_tx => {
      // New index only; deadFlag is optional so legacy voiceNotes rows just
      // won't have it — no data migration needed.
      return Promise.resolve();
    });

    // v16: rolls are unique only per municipality+cycle, so parcels move to a compound-key store
    // (Dexie cannot change a primary key in place). The upgrade runs before `parcels` is dropped.
    this.version(16).stores({
      fieldParcels: '[municipalityId+cycleId+id], id, municipalityId, cycleId, planId, [municipalityId+cycleId], [municipalityId+cycleId+planId], inspected',
      parcels: null,
    }).upgrade(upgradeParcelsToCompoundKey);
  }
}

export const db = new FieldInspectionDB();

// Handle version change requests from other tabs/connections
db.on('versionchange', () => {
  db.close();
});

// Non-destructive blocked handler — the root cause (index.html opening the DB
// without a version number) is fixed. This just logs for diagnostics.
db.on('blocked', () => {
  console.warn('[DB] Database upgrade blocked. Will resolve on next page load.');
});

/**
 * Nudge the service worker to fetch the newest bundle. Fire-and-forget: whatever
 * update it finds flows into the EXISTING usePWAUpdate refresh prompt
 * (use-pwa-update.ts: registerSW → onNeedRefresh → UpdateBanner) — this reuses
 * that mechanism rather than building a second one. Best-effort: no service
 * worker (dev / unsupported) is a safe no-op, and `navigator.serviceWorker.ready`
 * only resolves once an active worker exists (always true in the installed PWA
 * where this self-heal matters).
 */
function requestServiceWorkerUpdate(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  navigator.serviceWorker.ready
    .then((registration) => registration.update())
    .catch(() => {
      // Best-effort — the periodic use-pwa-update check retries hourly / on
      // visibilitychange.
    });
}

/**
 * Central handler for a failed `db.open()`. A `VersionError` (finding [39],
 * adjudicated 2026-07-10) means the running bundle is OLDER than the Dexie
 * schema already persisted on disk — i.e. a stale service worker is still
 * serving a superseded bundle after a newer deploy bumped the version (the 2c
 * manifest cache v13; see the v13 store above and the 2026-07-07 2c spec
 * amendment). Silently continuing would strand the field session in online-only
 * mode against a database it can never open, so instead log distinctly and nudge
 * the service worker to self-heal via the existing PWA refresh prompt. Any other
 * open failure keeps the original warn-and-degrade behavior.
 */
export function handleDbOpenError(err: unknown): void {
  if (err instanceof Error && err.name === 'VersionError') {
    console.error(
      '[DB] VersionError on open — a stale service worker is serving an old bundle ' +
        'against an upgraded database (v13 manifest cache). Triggering a service-worker ' +
        'update so the existing PWA refresh prompt can self-heal the field session.',
      err,
    );
    // Bypass use-pwa-update's 2-hour dismissal snooze for THIS heal: the session
    // is db-less, so an inspector who snoozed an unrelated update <2h ago must
    // still get the refresh prompt (Appendix B). The hook consumes this flag.
    setDbVersionErrorOverride();
    requestServiceWorkerUpdate();
    return;
  }
  console.warn('[DB] Failed to open database:', err);
  // App continues in online-only mode — offline features unavailable
}

// Eagerly open the database so it's ready when needed.
export const dbReady = db.open()
  .then(() => {})
  .catch((err) => {
    handleDbOpenError(err);
  });


/**
 * Check if database is available and working
 */
export async function checkDatabaseHealth(): Promise<boolean> {
  try {
    await db.settings.get('_health_check');
    return true;
  } catch {
    return false;
  }
}

/**
 * Reset database (delete all data and recreate)
 * Use when schema migration fails
 */
export async function resetDatabase(): Promise<void> {
  await db.delete();
  await db.open();
}

/**
 * Initialize database with a health check. Never deletes data: a failed
 * open/upgrade propagates to the caller (unsynced photos/notes must survive).
 */
export async function initDatabase(): Promise<void> {
  await dbReady;
  await db.photos.count();
}

/**
 * Check available storage space
 */
export async function getStorageEstimate(): Promise<{ used: number; quota: number } | null> {
  if (navigator.storage?.estimate) {
    const estimate = await navigator.storage.estimate();
    return {
      used: estimate.usage || 0,
      quota: estimate.quota || 0
    };
  }
  return null;
}

/**
 * Save a photo to IndexedDB
 */
export async function savePhoto(
  municipalityId: string,
  cycleId: string,
  parcelId: string,
  blob: Blob,
  buildingId?: string,
  geoData?: { latitude?: number; longitude?: number; heading?: number }
): Promise<Photo> {
  // Validate blob is an image
  if (!blob.type.startsWith('image/')) {
    throw new DatabaseError(`Invalid blob type: ${blob.type}. Expected image.`);
  }

  // Reject empty captures up front — a 0-byte photo is unrecoverable and would
  // otherwise render as a dead thumbnail and be rejected on sync as "Empty file".
  if (!hasImageBytes(blob)) {
    throw new DatabaseError('Refusing to save an empty (0-byte) photo. Please re-capture.');
  }

  // Persist the raw bytes, NOT the Blob: iOS/WebKit loses Blob payloads stored
  // in IndexedDB after navigation/eviction. Convert to ArrayBuffer BEFORE the
  // put — an IndexedDB transaction auto-commits across an await, so converting
  // inside one would throw InvalidStateError. See photo-blob.ts.
  const bytes = await blobToArrayBuffer(blob);

  const photo: Photo = {
    id: crypto.randomUUID(),
    municipalityId,
    cycleId,
    parcelId,
    ...(buildingId ? { buildingId } : {}),
    bytes,
    mimeType: blob.type,
    timestamp: Date.now(),
    synced: false,
    syncedFlag: 0,
    ...(geoData?.latitude != null ? { latitude: geoData.latitude } : {}),
    ...(geoData?.longitude != null ? { longitude: geoData.longitude } : {}),
    ...(geoData?.heading != null ? { heading: geoData.heading } : {}),
  };

  try {
    await db.photos.put(photo);
    return photo;
  } catch (err) {
    if (err instanceof Error && err.name === 'QuotaExceededError') {
      throw new StorageQuotaError();
    }
    throw new DatabaseError('Failed to save photo', err);
  }
}

/**
 * Get a single photo by ID
 */
export async function getPhoto(photoId: string): Promise<Photo | undefined> {
  try {
    return await db.photos.get(photoId);
  } catch (err) {
    throw new DatabaseError('Failed to get photo', err);
  }
}

/**
 * Get all photos for a parcel in a cycle (filtered by municipality for entity isolation)
 */
export async function getPhotosForParcel(municipalityId: string, cycleId: string, parcelId: string): Promise<Photo[]> {
  try {
    return await db.photos
      .where('[municipalityId+cycleId+parcelId]')
      .equals([municipalityId, cycleId, parcelId])
      .toArray();
  } catch (err) {
    throw new DatabaseError('Failed to get photos for parcel', err);
  }
}

/**
 * Get all unsynced photos
 */
export async function getUnsyncedPhotos(): Promise<Photo[]> {
  try {
    // Index-select unsynced rows (loads their bytes for upload) instead of
    // scanning the whole store with a predicate.
    return await db.photos.where('syncedFlag').equals(0).toArray();
  } catch (err) {
    throw new DatabaseError('Failed to get unsynced photos', err);
  }
}

/**
 * Photo counts grouped by parcel for a cycle, plus the ROUND-QUALIFYING
 * subset (Feature 7 §7.3). ONE walk of the [municipalityId+cycleId] index
 * builds both maps (`.each` streams rows as today — no new scans of the
 * multi-MB-bytes photos store):
 * - `counts`: ALL photos — today's semantics; feeds the totalPhotos display
 *   and the capture/delete increments, all unchanged.
 * - `qualifying`: photos taken after the parcel's round cutoff
 *   (`photo.timestamp > (cutoffs?.get(photo.parcelId) ?? 0)`). With no
 *   cutoffs the two maps are identical.
 */
export async function getPhotoCountsByParcel(
  municipalityId: string,
  cycleId: string,
  cutoffs?: Map<string, number>,
): Promise<{ counts: Map<string, number>; qualifying: Map<string, number> }> {
  const counts = new Map<string, number>();
  const qualifying = new Map<string, number>();

  try {
    await db.photos
      .where('[municipalityId+cycleId]')
      .equals([municipalityId, cycleId])
      .each(photo => {
        counts.set(photo.parcelId, (counts.get(photo.parcelId) || 0) + 1);
        if (photo.timestamp > (cutoffs?.get(photo.parcelId) ?? 0)) {
          qualifying.set(photo.parcelId, (qualifying.get(photo.parcelId) || 0) + 1);
        }
      });
    return { counts, qualifying };
  } catch (err) {
    throw new DatabaseError('Failed to get photo counts', err);
  }
}

/**
 * Rolls (Photo.parcelId) with ≥1 local photo in this muni+cycle — the
 * Photos-chip signal for RollSearchSheet's unmapped-cards list (spec §B2,
 * Kurt item 5: "5 unmapped, 4 have notes — find the 5th"). Index-only:
 * `.uniqueKeys()` (B2 review finding [4] — Dexie 4.2.1) over the existing
 * [municipalityId+cycleId+parcelId] compound index (present since photos-v9)
 * walks the index B-tree without ever deserializing a photo's multi-MB
 * `bytes` payload — same idiom as getLocalNoteSignals' scopedKeys walk over
 * voiceNotes below, except distinct-key: a roll with N photos contributes
 * ONE index entry to the walk instead of N (`.keys()`'s one-per-row), and the
 * result is already deduplicated before it ever reaches the `Set` build
 * below. No schema change: `parcelId` already IS the roll number for every
 * photo (see the Photo interface doc comment and
 * cardTargetFromMeta/cardTargetFromParcel, which both feed
 * `parcel.id`/`meta.roll` into savePhoto's parcelId param), so there is
 * nothing to derive — the existing compound index already keys on roll.
 */
export async function getRollsWithLocalPhotos(
  municipalityId: string,
  cycleId: string,
): Promise<Set<string>> {
  try {
    const keys = (await db.photos
      .where('[municipalityId+cycleId+parcelId]')
      .between([municipalityId, cycleId, Dexie.minKey], [municipalityId, cycleId, Dexie.maxKey])
      .uniqueKeys()) as unknown as Array<[string, string, string]>;
    return new Set(keys.map((key) => key[2]));
  } catch (err) {
    throw new DatabaseError('Failed to get rolls with local photos', err);
  }
}

/**
 * Mark a photo as synced
 */
export async function markPhotoSynced(photoId: string): Promise<void> {
  try {
    const updated = await db.photos.update(photoId, {
      synced: true,
      syncedFlag: 1,
      syncedAt: Date.now()
    });

    if (updated === 0) {
      throw new PhotoNotFoundError(photoId);
    }
  } catch (err) {
    if (err instanceof PhotoNotFoundError) throw err;
    throw new DatabaseError('Failed to mark photo synced', err);
  }
}

/**
 * Delete a photo
 */
export async function deletePhoto(photoId: string): Promise<void> {
  try {
    await db.photos.delete(photoId);
  } catch (err) {
    throw new DatabaseError('Failed to delete photo', err);
  }
}

/**
 * Clear synced photos (free up space) - efficient version using primaryKeys
 */
export async function clearSyncedPhotos(): Promise<number> {
  try {
    // primaryKeys() off an indexed .where() reads keys straight from the index
    // — no record values (image bytes) are deserialized.
    const ids = await db.photos.where('syncedFlag').equals(1).primaryKeys();
    await db.photos.bulkDelete(ids as string[]);
    return ids.length;
  } catch (err) {
    throw new DatabaseError('Failed to clear synced photos', err);
  }
}

/**
 * Save parcels to IndexedDB for offline use
 * M5 fix: Wrapped in explicit transaction for data integrity
 */
export async function saveParcels(parcels: Parcel[]): Promise<void> {
  try {
    await db.transaction('rw', db.fieldParcels, async () => {
      await db.fieldParcels.bulkPut(parcels);
    });
  } catch (err) {
    throw new DatabaseError('Failed to save parcels', err);
  }
}

/**
 * Save parcels for a specific cycle, replacing any existing ones
 * Used when fetching fresh data from API
 */
export async function saveParcelsForCycle(
  municipalityId: string,
  cycleId: string,
  parcels: Parcel[]
): Promise<void> {
  try {
    await db.transaction('rw', db.fieldParcels, async () => {
      // Delete existing parcels for this cycle first
      await db.fieldParcels
        .where('[municipalityId+cycleId]')
        .equals([municipalityId, cycleId])
        .delete();

      // Save new parcels
      if (parcels.length > 0) {
        await db.fieldParcels.bulkPut(parcels);
      }
    });
  } catch (err) {
    throw new DatabaseError('Failed to save parcels for cycle', err);
  }
}

/**
 * Get all parcels for a municipality
 */
export async function getParcelsForMunicipality(municipalityId: string): Promise<Parcel[]> {
  try {
    return await db.fieldParcels.where('municipalityId').equals(municipalityId).toArray();
  } catch (err) {
    throw new DatabaseError('Failed to get parcels', err);
  }
}

/**
 * Get total pending sync count (all municipalities)
 */
export async function getPendingSyncCount(): Promise<number> {
  try {
    // Index count — no record values (image bytes) are read.
    return await db.photos.where('syncedFlag').equals(0).count();
  } catch (err) {
    throw new DatabaseError('Failed to get pending sync count', err);
  }
}

/**
 * Get pending sync count for a specific cycle (entity-isolated)
 */
export async function getPendingSyncCountByCycle(municipalityId: string, cycleId: string): Promise<number> {
  try {
    // Pure index count via [municipalityId+cycleId+syncedFlag]. This runs on the
    // hot path — after every photo capture (refreshPendingCounts) and on each
    // auto-sync tick — so it must not deserialize photo records / image bytes.
    return await db.photos
      .where('[municipalityId+cycleId+syncedFlag]')
      .equals([municipalityId, cycleId, 0])
      .count();
  } catch (err) {
    throw new DatabaseError('Failed to get pending sync count by cycle', err);
  }
}

/**
 * Count field-discovered buildings that have not yet been uploaded.
 * Mirrors getPendingSyncCountByCycle but for buildings; used by the
 * auto-sync hook to decide whether to flush buildings before photos.
 */
export async function getPendingBuildingSyncCount(
  municipalityId: string,
  cycleId: string,
): Promise<number> {
  try {
    return await db.buildings
      .where('[municipalityId+cycleId]')
      .equals([municipalityId, cycleId])
      .filter(b => b.source === 'field_discovered' && b.syncedAt === 0)
      .count();
  } catch (err) {
    throw new DatabaseError('Failed to get pending building sync count', err);
  }
}

/**
 * Get unsynced photos for a specific cycle (entity-isolated)
 */
export async function getUnsyncedPhotosByCycle(municipalityId: string, cycleId: string): Promise<Photo[]> {
  try {
    // Index-select this cycle's unsynced rows via [municipalityId+cycleId+
    // syncedFlag]. Loads bytes for the rows it returns (they're about to be
    // uploaded), but never scans synced rows.
    return await db.photos
      .where('[municipalityId+cycleId+syncedFlag]')
      .equals([municipalityId, cycleId, 0])
      .toArray();
  } catch (err) {
    throw new DatabaseError('Failed to get unsynced photos by cycle', err);
  }
}

/**
 * Mark multiple photos as synced in a transaction
 */
export async function markPhotosSynced(photoIds: string[]): Promise<void> {
  try {
    await db.transaction('rw', db.photos, async () => {
      const now = Date.now();
      for (const id of photoIds) {
        await db.photos.update(id, { synced: true, syncedFlag: 1, syncedAt: now });
      }
    });
  } catch (err) {
    throw new DatabaseError('Failed to mark photos synced', err);
  }
}

// ============================================================================
// Inspection Plan Functions
// ============================================================================

/**
 * Save an inspection plan to IndexedDB
 */
export async function saveInspectionPlan(plan: InspectionPlan): Promise<void> {
  try {
    await db.inspectionPlans.put(plan);
  } catch (err) {
    throw new DatabaseError('Failed to save inspection plan', err);
  }
}

/**
 * Get a single inspection plan by ID
 */
export async function getInspectionPlan(planId: string): Promise<InspectionPlan | undefined> {
  try {
    return await db.inspectionPlans.get(planId);
  } catch (err) {
    throw new DatabaseError('Failed to get inspection plan', err);
  }
}

/**
 * Get all inspection plans for a cycle (entity-isolated)
 */
export async function getInspectionPlansForCycle(municipalityId: string, cycleId: string): Promise<InspectionPlan[]> {
  try {
    return await db.inspectionPlans
      .where('[municipalityId+cycleId]')
      .equals([municipalityId, cycleId])
      .toArray();
  } catch (err) {
    throw new DatabaseError('Failed to get inspection plans for cycle', err);
  }
}

/**
 * Update an inspection plan's parcelsDownloaded and syncedAt fields
 */
export async function updateInspectionPlanSyncStatus(
  planId: string,
  parcelsDownloaded: boolean,
  syncedAt: number
): Promise<void> {
  try {
    const updated = await db.inspectionPlans.update(planId, {
      parcelsDownloaded,
      syncedAt
    });
    if (updated === 0) {
      throw new DatabaseError(`Inspection plan not found: ${planId}`);
    }
  } catch (err) {
    if (err instanceof DatabaseError) throw err;
    throw new DatabaseError('Failed to update inspection plan sync status', err);
  }
}

/**
 * Delete an inspection plan from IndexedDB
 */
export async function deleteInspectionPlan(planId: string): Promise<void> {
  try {
    await db.inspectionPlans.delete(planId);
  } catch (err) {
    throw new DatabaseError('Failed to delete inspection plan', err);
  }
}

// ============================================================================
// Enhanced Parcel Functions
// ============================================================================

/**
 * Mark a parcel as inspected (has photos)
 */
export async function markParcelInspected(municipalityId: string, cycleId: string, parcelId: string): Promise<void> {
  try {
    const key: ParcelKey = [municipalityId, cycleId, String(parcelId)];
    const updated = await db.fieldParcels.update(key, { inspected: true });
    if (updated === 0) {
      throw new DatabaseError(`Parcel not found: ${parcelId}`);
    }
  } catch (err) {
    if (err instanceof DatabaseError) throw err;
    throw new DatabaseError('Failed to mark parcel as inspected', err);
  }
}

/**
 * Mark a parcel as not inspected (no photos remaining)
 */
export async function markParcelUninspected(municipalityId: string, cycleId: string, parcelId: string): Promise<void> {
  try {
    const key: ParcelKey = [municipalityId, cycleId, String(parcelId)];
    const updated = await db.fieldParcels.update(key, { inspected: false });
    if (updated === 0) {
      throw new DatabaseError(`Parcel not found: ${parcelId}`);
    }
  } catch (err) {
    if (err instanceof DatabaseError) throw err;
    throw new DatabaseError('Failed to mark parcel as uninspected', err);
  }
}

/**
 * Mark a parcel as carrying notes (≥1 live card mark or voice note, local
 * or server-seeded). Notes analog of markParcelInspected — the merged
 * `hasNotes` flag follows the `inspected` lifecycle (fieldmap 4-state §4.2).
 */
export async function markParcelHasNotes(municipalityId: string, cycleId: string, parcelId: string): Promise<void> {
  try {
    const key: ParcelKey = [municipalityId, cycleId, String(parcelId)];
    const updated = await db.fieldParcels.update(key, { hasNotes: true });
    if (updated === 0) {
      throw new DatabaseError(`Parcel not found: ${parcelId}`);
    }
  } catch (err) {
    if (err instanceof DatabaseError) throw err;
    throw new DatabaseError('Failed to mark parcel as having notes', err);
  }
}

/**
 * Mark a parcel as carrying no notes (last note evidence gone). Notes
 * analog of markParcelUninspected.
 */
export async function markParcelNoNotes(municipalityId: string, cycleId: string, parcelId: string): Promise<void> {
  try {
    const key: ParcelKey = [municipalityId, cycleId, String(parcelId)];
    const updated = await db.fieldParcels.update(key, { hasNotes: false });
    if (updated === 0) {
      throw new DatabaseError(`Parcel not found: ${parcelId}`);
    }
  } catch (err) {
    if (err instanceof DatabaseError) throw err;
    throw new DatabaseError('Failed to mark parcel as having no notes', err);
  }
}

/**
 * Field-level applier for the status-pull wave (status-pull.service.ts):
 * writes the DURABLE server-seeded signals (`serverHasNotes`/
 * `serverPhotoCount`) from a `GET /parcels/status` poll, never the merged
 * local-evidence fields (`hasNotes`/`inspected`/`syncedPhotoCount`) that
 * ONLY the round-sweep appliers above (markParcelHasNotes/NoNotes/
 * Inspected/Uninspected) own — a status row is a signal for the sweep to
 * reconsider, not a direct write to what's displayed.
 *
 * Unlike those sweep appliers, a row absent locally is NOT an error: it
 * just means this device never downloaded that roll (a status poll
 * naturally covers the whole cycle, not just this device's local subset),
 * so it's silently skipped.
 *
 * The returned count is the number of rows whose `serverHasNotes`/
 * `serverPhotoCount` actually CHANGED and were written — absent rows,
 * cross-cycle rows AND rows whose durable signal already matches the
 * server's are all skipped and uncounted. `FieldInspectionPage.
 * handleStatusPull` relies on `0` to skip the rest of the pull (re-reading
 * the cycle and walking photo bytes), so the twice-trigger requirement is
 * now "no Dexie write, no photo walk" rather than merely "no flip": Dexie's
 * `update()` is a whole-record read-modify-write — a `Parcel` carries its
 * boundary geometry — so writing every row on every poll would rewrite the
 * entire cycle's parcels roughly every 75 s, mid-inking, for no reason.
 *
 * `photoCount` ABSENT (not 0) never counts as a change and leaves
 * `serverPhotoCount` untouched — the same absent-vs-zero contract
 * `Parcel.serverPhotoCount`'s doc comment and computeInspectedSweep
 * (round-sweep.ts) already depend on; only write it when the row actually
 * carries the field AND that field differs from what's stored. An absent
 * (`undefined`) `serverHasNotes` compared against `false` DOES count as a
 * change, though, so the first pull still seeds the field exactly as
 * before.
 *
 * Rows are keyed by (municipalityId, cycleId, roll), so a stale status pull
 * for another municipality/cycle (see FieldInspectionPage.tsx's
 * routeParamsRef) simply misses: treated like an absent row — skipped, not
 * counted, not an error.
 *
 * Batches every row write inside ONE `db.transaction('rw', db.fieldParcels,
 * ...)` regardless of row count, per the plan's Global Constraints.
 */
export async function applyServerParcelStatuses(
  municipalityId: string,
  cycleId: string,
  rows: Array<{ id: string; hasNotes: boolean; photoCount?: number }>,
): Promise<number> {
  let applied = 0;
  try {
    await db.transaction('rw', db.fieldParcels, async () => {
      for (const row of rows) {
        const key: ParcelKey = [municipalityId, cycleId, String(row.id)];
        const existing = await db.fieldParcels.get(key);
        if (!existing) continue;
        const nextHasNotes = Boolean(row.hasNotes);
        const changes: Partial<Pick<Parcel, 'serverHasNotes' | 'serverPhotoCount'>> = {};
        if (existing.serverHasNotes !== nextHasNotes) changes.serverHasNotes = nextHasNotes;
        if (row.photoCount !== undefined && existing.serverPhotoCount !== row.photoCount) {
          changes.serverPhotoCount = row.photoCount;
        }
        // Unchanged row: no write, not counted. Dexie's update() is a whole-
        // record read-modify-write (a Parcel carries its boundary geometry),
        // so a no-op write here is NOT free — at ~75 s per pull it was
        // rewriting every parcel of the cycle mid-inking, and a never-zero
        // `applied` made the caller re-walk photo bytes on every pull.
        if (Object.keys(changes).length === 0) continue;
        await db.fieldParcels.update(key, changes);
        applied++;
      }
    });
    return applied;
  } catch (err) {
    throw new DatabaseError('Failed to apply server parcel statuses', err);
  }
}

/**
 * Atomically mark a photo synced AND credit its parcel's syncedPhotoCount /
 * inspected flag in ONE transaction.
 *
 * Replaces the markPhotoSynced-per-photo + updateParcelSyncedCount-after-the-
 * loop split in the sync services: a page kill mid-loop (iOS jetsam during a
 * photo-heavy inspection) flagged photos synced WITHOUT the parcel credit,
 * and the next run's global clearSyncedPhotos() then erased the records —
 * leaving the load-time reconciler seeing "inspected, no local photos, no
 * synced count" and flipping the parcel gray despite its photos being safely
 * on the server (Beaumont, 2026-06-08/09).
 *
 * Idempotent: re-calling for an already-synced photo refreshes the photo's
 * synced flags (syncedAt is updated) but never double-credits the parcel and
 * never re-stamps syncedPhotoEvidence.
 *
 * A missing parcel row (plan cleared while the photo was pending) is NOT an
 * error: the photo is still marked synced so the upload queue cannot wedge —
 * only the parcel credit is skipped.
 *
 * B2 review finding [1] (round 2): syncedPhotoEvidence (RecordCardMetaRow —
 * see that field's doc comment) is stamped UNCONDITIONALLY below on every
 * newly-synced photo, not only when the parcel lookup misses. The common
 * real ordering is the OPPOSITE of "parcel already gone": the parcel row
 * still exists when the photo syncs (crediting Parcel.syncedPhotoCount),
 * clearSyncedPhotos then bulk-deletes the now-synced `photos` row, and only
 * LATER does the parcel row itself get deleted (clearParcelsForPlan in
 * parcel-sync.service.ts, deleteParcelsForCycle in FieldInspectionTab) — at
 * which point Parcel.syncedPhotoCount's credit is gone too, with nothing
 * left to prove the roll was ever photographed. Stamping only in the
 * parcel-missing branch (the rarer ordering: plan cleared while the photo
 * was still pending) missed this far more common case entirely. `.update()`
 * against a missing recordCards meta id is a silent no-op (Dexie contract):
 * a roll with neither a parcel nor a downloaded card can never appear in the
 * RollSearchSheet unmapped-cards list this flag feeds, so the unconditional
 * stamp is a tested no-op for that case, not a new risk.
 *
 * Called once per photo in a sequential upload loop; the per-call IDB
 * transaction overhead is dominated by the network upload cost — do not call
 * in a tight loop without that amortization.
 *
 * F2 photo-half fix round 1 (Kurt 2026-08-21): also bumps
 * `Parcel.serverPhotoCount` alongside `syncedPhotoCount`, but ONLY when it
 * is already present on the row — see the inline comment at the update
 * call for the full lifecycle-regression rationale (a same-session
 * download → capture → sync → clearSyncedPhotos sequence was clearing
 * parcels photographed and uploaded THAT SESSION, since the server's
 * `photo_count` is a non-Optional int always sent on download, so
 * `serverPhotoCount` starts PRESENT-as-0 that morning, not absent).
 */
export async function markPhotoSyncedAndBumpParcel(
  photoId: string,
  parcelId: string,
): Promise<void> {
  try {
    await db.transaction('rw', db.photos, db.fieldParcels, db.recordCards, async () => {
      const photo = await db.photos.get(photoId);
      if (!photo) {
        throw new PhotoNotFoundError(photoId);
      }
      const alreadySynced = photo.synced === true;
      await db.photos.update(photoId, {
        synced: true,
        syncedFlag: 1,
        syncedAt: Date.now(),
      });
      if (!alreadySynced) {
        const key: ParcelKey = [photo.municipalityId, photo.cycleId, String(parcelId)];
        const parcel = await db.fieldParcels.get(key);
        if (parcel) {
          await db.fieldParcels.update(key, {
            syncedPhotoCount: (parcel.syncedPhotoCount || 0) + 1,
            inspected: true,
            // F2 photo-half fix round 1 (Kurt 2026-08-21, reviewer-prescribed
            // lifecycle fix): bump serverPhotoCount alongside the
            // syncedPhotoCount credit, ONLY when it is already PRESENT
            // (`!== undefined`) — a pre-upgrade row (never re-downloaded
            // since the serverPhotoCount fix shipped) stays absent and keeps
            // reading the legacy syncedPhotoCount path in
            // computeInspectedSweep, exactly as designed.
            //
            // Without this, serverPhotoCount is present-but-stale for every
            // parcel downloaded fresh this morning (backend `photo_count` is
            // a non-Optional int — the field is ALWAYS sent, so
            // toLocalParcel seeds serverPhotoCount=0 the instant a parcel is
            // downloaded, not "absent until re-download" as the merge-layer
            // fix assumed). The assessor then photographs it; this photo
            // syncs and credits syncedPhotoCount, but the stale
            // serverPhotoCount=0 sits untouched; clearSyncedPhotos() (called
            // by every syncAll/reconcile-sync run, clearAfterSync defaulting
            // true everywhere) then purges the now-synced local `photos`
            // rows, leaving `qualifying` empty on the next map load. The
            // sweep's present-branch (serverPhotoCount !== undefined) wins
            // and reads the stale 0 — clearing a parcel photographed and
            // uploaded THIS SESSION.
            //
            // The bump is round-correct: this call fires only once the
            // server has CONFIRMED the upload (markPhotoSyncedAndBumpParcel
            // is called post-upload, inside the same transaction as the
            // synced-flag flip), so a photo synced now is by definition
            // taken after any prior round cutoff — it is exactly the kind
            // of evidence the server's own round-filtered photo_count would
            // count on the NEXT export. Deletes-clear is still preserved:
            // a later re-download always adopts the server's live count
            // VERBATIM (parcel-sync.service.ts's merge sites), including 0
            // after an office-side delete — this local increment is only a
            // bridge between re-downloads, never a floor under one.
            ...(parcel.serverPhotoCount !== undefined
              ? { serverPhotoCount: parcel.serverPhotoCount + 1 }
              : {}),
          });
        } else {
          console.warn(
            `[db] markPhotoSyncedAndBumpParcel: parcel ${parcelId} not in local cache; photo marked synced without parcel credit`,
          );
        }
        // B2 review finding [1] (round 2): stamp durable Photos-chip
        // evidence UNCONDITIONALLY here — not just on the parcel-missing
        // branch above — so the common ordering (parcel still present now,
        // deleted later) also leaves durable proof behind. See this
        // function's and RecordCardMetaRow.syncedPhotoEvidence's doc
        // comments for why. `.update()` against a missing meta id is a
        // silent Dexie no-op.
        await db.recordCards.update(`${photo.municipalityId}_${key[2]}`, {
          syncedPhotoEvidence: 1,
        });
      }
    });
  } catch (err) {
    if (err instanceof PhotoNotFoundError) throw err;
    throw new DatabaseError('Failed to mark photo synced with parcel credit', err);
  }
}

/**
 * Get all parcels for a specific inspection plan (entity-isolated)
 */
export async function getParcelsForPlan(
  municipalityId: string,
  cycleId: string,
  planId: string
): Promise<Parcel[]> {
  try {
    return await db.fieldParcels
      .where('[municipalityId+cycleId+planId]')
      .equals([municipalityId, cycleId, planId])
      .toArray();
  } catch (err) {
    throw new DatabaseError('Failed to get parcels for plan', err);
  }
}

/**
 * Get all parcels for a cycle (entity-isolated)
 */
export async function getParcelsForCycle(
  municipalityId: string,
  cycleId: string
): Promise<Parcel[]> {
  try {
    return await db.fieldParcels
      .where('[municipalityId+cycleId]')
      .equals([municipalityId, cycleId])
      .toArray();
  } catch (err) {
    throw new DatabaseError('Failed to get parcels for cycle', err);
  }
}

/**
 * Count parcels for a cycle without loading rows (entity-isolated)
 */
export async function countParcelsForCycle(
  municipalityId: string,
  cycleId: string
): Promise<number> {
  try {
    return await db.fieldParcels
      .where('[municipalityId+cycleId]')
      .equals([municipalityId, cycleId])
      .count();
  } catch (err) {
    throw new DatabaseError('Failed to count parcels for cycle', err);
  }
}

/**
 * Delete all parcels for a specific plan
 */
export async function deleteParcelsForPlan(planId: string): Promise<number> {
  try {
    const ids = await db.fieldParcels.where('planId').equals(planId).primaryKeys();
    await db.fieldParcels.bulkDelete(ids);
    return ids.length;
  } catch (err) {
    throw new DatabaseError('Failed to delete parcels for plan', err);
  }
}

/**
 * Delete all parcels for a specific cycle (regardless of plan)
 */
export async function deleteParcelsForCycle(municipalityId: string, cycleId: string): Promise<number> {
  try {
    const ids = await db.fieldParcels
      .where('[municipalityId+cycleId]')
      .equals([municipalityId, cycleId])
      .primaryKeys();
    await db.fieldParcels.bulkDelete(ids);
    return ids.length;
  } catch (err) {
    throw new DatabaseError('Failed to delete parcels for cycle', err);
  }
}

export async function pruneUnplannedParcelsNotIn(municipalityId: string, cycleId: string, serverRolls: Set<string>): Promise<number> {
  try {
    const unplannedKeys = await db.fieldParcels.where('[municipalityId+cycleId+planId]')
      .equals([municipalityId, cycleId, UNPLANNED_PLAN_ID]).primaryKeys();
    const staleIds = unplannedKeys.filter(key => !serverRolls.has(key[2]));
    if (staleIds.length === 0) return 0;
    const stale = (await db.fieldParcels.bulkGet(staleIds)).filter((p): p is Parcel => !!p);
    const photoRolls = await getRollsWithLocalPhotos(municipalityId, cycleId);
    const { noteRolls, voicePropertyIds } = await getLocalNoteSignals(municipalityId, cycleId);
    const pendingBuildingProps = new Set((await db.buildings.where('[municipalityId+cycleId]')
      .equals([municipalityId, cycleId]).filter(b => b.source === 'field_discovered' && b.syncedAt === 0).toArray())
      .map(b => b.propertyId));
    const ids = stale.filter(p =>
      !photoRolls.has(p.id) && !noteRolls.has(p.id) &&
      !voicePropertyIds.has(p.id) && !(p.propertyId && voicePropertyIds.has(p.propertyId)) &&
      !pendingBuildingProps.has(p.propertyId ?? p.id)).map((p): ParcelKey => [p.municipalityId, p.cycleId, p.id]);
    await db.fieldParcels.bulkDelete(ids);
    return ids.length;
  } catch (err) {
    throw new DatabaseError('Failed to prune unplanned parcels', err);
  }
}

/**
 * Get parcel inspection counts for a plan
 * Returns count of inspected (green) vs not inspected (gray) parcels
 */
export async function getParcelInspectionCounts(
  municipalityId: string,
  cycleId: string,
  planId: string
): Promise<{ notInspected: number; inspected: number; total: number }> {
  try {
    const parcels = await getParcelsForPlan(municipalityId, cycleId, planId);
    const inspected = parcels.filter(p => p.inspected).length;
    return {
      notInspected: parcels.length - inspected,
      inspected,
      total: parcels.length
    };
  } catch (err) {
    throw new DatabaseError('Failed to get parcel inspection counts', err);
  }
}

// ============================================================================
// Municipality & Cycle Cache Functions
// ============================================================================

/**
 * Cache municipality data for offline use
 * Non-throwing - silently fails if database not ready
 */
export async function cacheMunicipality(id: string, name: string): Promise<void> {
  try {
    await db.municipalities.put({
      id,
      name,
      cachedAt: Date.now()
    });
  } catch (err) {
    // Silent fail - caching is optional
    console.warn('Failed to cache municipality:', err);
  }
}

/**
 * Get cached municipality data
 * Returns undefined if not found or database error
 */
export async function getCachedMunicipality(id: string): Promise<CachedMunicipality | undefined> {
  try {
    return await db.municipalities.get(id);
  } catch (err) {
    console.warn('Failed to get cached municipality:', err);
    return undefined;
  }
}

/**
 * Cache cycle data for offline use
 * Non-throwing - silently fails if database not ready
 */
export async function cacheCycle(
  id: string,
  municipalityId: string,
  name: string,
  year: number,
  status: string
): Promise<void> {
  try {
    await db.cycles.put({
      id,
      municipalityId,
      name,
      year,
      status,
      cachedAt: Date.now()
    });
  } catch (err) {
    // Silent fail - caching is optional
    console.warn('Failed to cache cycle:', err);
  }
}

/**
 * Get cached cycle data
 * Returns undefined if not found or database error
 */
export async function getCachedCycle(id: string): Promise<CachedCycle | undefined> {
  try {
    return await db.cycles.get(id);
  } catch (err) {
    console.warn('Failed to get cached cycle:', err);
    return undefined;
  }
}

// ============================================================================
// Dashboard Summary Cache Functions
// ============================================================================

/**
 * Cache dashboard summary response for offline use.
 * Non-throwing - silently fails if database not ready.
 */
export async function cacheDashboardSummary(data: unknown): Promise<void> {
  try {
    await db.settings.put({
      key: 'dashboardSummary',
      value: { ...(data as Record<string, unknown>), cachedAt: Date.now() } as Record<string, unknown>,
    });
  } catch {
    // Non-critical — caching is optional
  }
}

/**
 * Get cached dashboard summary.
 * Returns null if not found or database error.
 */
export async function getCachedDashboardSummary(): Promise<DashboardSummary | null> {
  try {
    const entry = await db.settings.get('dashboardSummary');
    if (!entry || typeof entry.value !== 'object' || entry.value === null) {
      return null;
    }
    const cached = entry.value as Record<string, unknown>;
    if (!Array.isArray(cached.activeCycles)) {
      return null;
    }
    return cached as unknown as DashboardSummary;
  } catch {
    return null;
  }
}

// ============================================================================
// Status-pull delta cursor (status-pull.service.ts)
// ============================================================================

// A type alias, not an interface: only the former is assignable to
// Setting.value's `Record<string, unknown>` without a copy.
export type StatusPullCursor = {
  syncedAt: number;          // ms of the pull's synced_at: the next `since=`
  roundsFingerprint: string; // echoed back; a rounds edit makes the server go full
  storedAt: number;          // client clock at the last FULL pull (skew-free 24 h clock)
};

export async function getStatusPullCursor(
  municipalityId: string,
  cycleId: string,
): Promise<StatusPullCursor | undefined> {
  const entry = await db.settings.get(`statusPull:${municipalityId}:${cycleId}`);
  return entry?.value as StatusPullCursor | undefined;
}

export async function setStatusPullCursor(
  municipalityId: string,
  cycleId: string,
  cursor: StatusPullCursor,
): Promise<void> {
  await db.settings.put({ key: `statusPull:${municipalityId}:${cycleId}`, value: cursor });
}

// Set when a field download starts, cleared only on full parcel success: a
// restart mid-download must not leave the "Downloaded" badge unchallenged.
export async function isCycleDownloadIncomplete(municipalityId: string, cycleId: string): Promise<boolean> {
  return !!(await db.settings.get(`cycle-download-incomplete:${municipalityId}:${cycleId}`));
}

export async function setCycleDownloadIncomplete(
  municipalityId: string,
  cycleId: string,
  incomplete: boolean,
): Promise<void> {
  const key = `cycle-download-incomplete:${municipalityId}:${cycleId}`;
  if (incomplete) await db.settings.put({ key, value: { startedAt: Date.now() } });
  else await db.settings.delete(key);
}

/**
 * Get all cached cycles for a municipality
 */
export async function getCachedCyclesForMunicipality(municipalityId: string): Promise<CachedCycle[]> {
  try {
    return await db.cycles.where('municipalityId').equals(municipalityId).toArray();
  } catch (err) {
    console.warn('Failed to get cached cycles:', err);
    return [];
  }
}

// ============================================================================
// Voice Note Functions
// ============================================================================

/**
 * Save a voice note to IndexedDB
 */
export async function saveVoiceNote(
  municipalityId: string,
  cycleId: string,
  propertyId: string,
  blob: Blob,
  durationSeconds: number,
  mimeType: string,
  recordedIn: VoiceNoteRecordedIn,
  buildingId?: string
): Promise<VoiceNote> {
  if (!blob.type.startsWith('audio/') && blob.type !== '') {
    throw new DatabaseError(`Invalid blob type: ${blob.type}. Expected audio.`);
  }

  // Reject empty captures up front — a 0-byte recording is unrecoverable and
  // would be rejected on sync as a 400 ("File too small to be a valid audio file").
  if (!hasImageBytes(blob)) {
    throw new DatabaseError('Refusing to save an empty (0-byte) voice note. Please re-record.');
  }

  // Persist the raw bytes, NOT the Blob: iOS/WebKit loses Blob payloads stored
  // in IndexedDB (the bytes read back as 0 after navigation/eviction). Convert
  // to ArrayBuffer BEFORE the put — an IndexedDB transaction auto-commits across
  // an await, so converting inside one would throw InvalidStateError. See photo-blob.ts.
  const bytes = await blobToArrayBuffer(blob);

  const voiceNote: VoiceNote = {
    id: crypto.randomUUID(),
    municipalityId,
    cycleId,
    propertyId,
    ...(buildingId ? { buildingId } : {}),
    bytes,
    durationSeconds,
    mimeType,
    recordedIn,
    synced: false,
    timestamp: Date.now()
  };

  try {
    await db.voiceNotes.put(voiceNote);
    return voiceNote;
  } catch (err) {
    if (err instanceof Error && err.name === 'QuotaExceededError') {
      throw new StorageQuotaError();
    }
    throw new DatabaseError('Failed to save voice note', err);
  }
}

/**
 * recordedIn of a note saved before the field existed: 'card' when a local voice dot references it (saved on
 * the row), otherwise undefined so the server's fallback decides.
 */
export async function healVoiceNoteRecordedIn(note: VoiceNote): Promise<VoiceNoteRecordedIn | undefined> {
  if (note.recordedIn) return note.recordedIn;
  const mark = await db.cardMarks
    .where('municipalityId')
    .equals(note.municipalityId)
    .filter((m) => m.voiceNoteId === note.id)
    .first();
  if (!mark) return undefined;
  await db.voiceNotes.update(note.id, { recordedIn: 'card' });
  return 'card';
}

/**
 * Get all voice notes for a property in a cycle (entity-isolated)
 */
export async function getVoiceNotesForProperty(
  municipalityId: string,
  cycleId: string,
  propertyId: string
): Promise<VoiceNote[]> {
  try {
    const notes = await db.voiceNotes
      .where('[municipalityId+cycleId+propertyId]')
      .equals([municipalityId, cycleId, propertyId])
      .toArray();
    return notes.sort((a, b) => b.timestamp - a.timestamp);
  } catch (err) {
    throw new DatabaseError('Failed to get voice notes for property', err);
  }
}

/**
 * Get all unsynced voice notes for a specific cycle (entity-isolated)
 */
export async function getUnsyncedVoiceNotesByCycle(
  municipalityId: string,
  cycleId: string
): Promise<VoiceNote[]> {
  try {
    return await db.voiceNotes
      .where('[municipalityId+cycleId]')
      .equals([municipalityId, cycleId])
      .filter(note => !note.synced)
      .toArray();
  } catch (err) {
    throw new DatabaseError('Failed to get unsynced voice notes by cycle', err);
  }
}

/**
 * Mark a voice note as synced
 */
export async function markVoiceNoteSynced(noteId: string): Promise<void> {
  try {
    const updated = await db.voiceNotes.update(noteId, {
      synced: true,
      syncedAt: Date.now(),
      // Kurt item 8 residual (spec §C4) — healing race: the missing-blob
      // path in voice-sync.service.ts can dead-flag a note whose iOS Blob
      // was purged AFTER it already reached the server (app killed between
      // a successful upload and this exact write; reconcile-sync.service.ts
      // calls this same function when its cross-check discovers the server
      // already has a locally-pending note). Server confirmation — arriving
      // here either way — is definitive proof the note is NOT dead, so
      // clear it unconditionally. Without this, a note that genuinely IS on
      // the server could stay permanently excluded from Notes evidence,
      // which is worse than the stale-paint bug this task fixes.
      deadFlag: 0,
    });

    if (updated === 0) {
      throw new VoiceNoteNotFoundError(noteId);
    }
  } catch (err) {
    if (err instanceof VoiceNoteNotFoundError) throw err;
    throw new DatabaseError('Failed to mark voice note synced', err);
  }
}

/**
 * Quarantine a voice note that voice-sync has determined can never succeed —
 * audio bytes permanently gone (iOS Blob purge) or a permanent non-retryable
 * 400 (Kurt item 8 residual, spec §C4). deadFlag is the ONLY change; `synced`
 * stays false — there is no server copy, so this must never fabricate a
 * synced state.
 */
export async function markVoiceNoteDead(noteId: string): Promise<void> {
  try {
    const updated = await db.voiceNotes.update(noteId, { deadFlag: 1 });

    if (updated === 0) {
      throw new VoiceNoteNotFoundError(noteId);
    }
  } catch (err) {
    if (err instanceof VoiceNoteNotFoundError) throw err;
    throw new DatabaseError('Failed to mark voice note dead', err);
  }
}

/**
 * Delete a voice note
 */
export async function deleteVoiceNote(noteId: string): Promise<void> {
  try {
    await db.voiceNotes.delete(noteId);
  } catch (err) {
    throw new DatabaseError('Failed to delete voice note', err);
  }
}

/**
 * Get pending voice note sync count for a specific cycle (entity-isolated)
 */
export async function getPendingVoiceNoteSyncCount(
  municipalityId: string,
  cycleId: string
): Promise<number> {
  try {
    return await db.voiceNotes
      .where('[municipalityId+cycleId]')
      .equals([municipalityId, cycleId])
      .filter(note => !note.synced)
      .count();
  } catch (err) {
    throw new DatabaseError('Failed to get pending voice note sync count', err);
  }
}

// ============================================================================
// Cycle-wide Parcel Functions (Ad-hoc Inspection Support)
// ============================================================================

/**
 * Get only planned parcels for a cycle (excludes __cycle__ and online sentinels)
 */
export async function getPlannedParcelsForCycle(
  municipalityId: string,
  cycleId: string
): Promise<Parcel[]> {
  try {
    return await db.fieldParcels
      .where('[municipalityId+cycleId]')
      .equals([municipalityId, cycleId])
      .filter(p => p.planId !== UNPLANNED_PLAN_ID && p.planId !== ONLINE_PLAN_ID)
      .toArray();
  } catch (err) {
    throw new DatabaseError('Failed to get planned parcels for cycle', err);
  }
}

/**
 * Get only unplanned (cycle background) parcels for a cycle
 */
export async function getUnplannedParcelsForCycle(
  municipalityId: string,
  cycleId: string
): Promise<Parcel[]> {
  try {
    return await db.fieldParcels
      .where('[municipalityId+cycleId+planId]')
      .equals([municipalityId, cycleId, UNPLANNED_PLAN_ID])
      .toArray();
  } catch (err) {
    throw new DatabaseError('Failed to get unplanned parcels for cycle', err);
  }
}

/**
 * Get a single parcel by its compound primary key (municipality, cycle,
 * roll number) — a point lookup, cheap enough to call from a card-viewer's
 * missing-card panel without pulling the whole cycle. Returns undefined when
 * no row exists in that scope (e.g. a card-only roll-search target with no
 * mapped parcel).
 */
export async function getParcelById(
  municipalityId: string,
  cycleId: string,
  id: string
): Promise<Parcel | undefined> {
  try {
    return await db.fieldParcels.get([municipalityId, cycleId, id]);
  } catch (err) {
    throw new DatabaseError('Failed to get parcel by id', err);
  }
}

// ============================================================================
// Building Query Functions
// ============================================================================

/**
 * Get the locally-cached buildings for a property (entity-isolated).
 *
 * Buildings are downloaded into IndexedDB for EVERY cycle property at plan
 * download time (syncBuildingsForCycle), keyed by the property's Firestore doc
 * id (NOT the roll number). This reads them straight from the local store so
 * the offline cross-roll target picker can list a target roll's buildings
 * without a network call. Returns [] for a property whose buildings were never
 * downloaded (e.g. before the first download).
 */
export async function getBuildingsForProperty(
  municipalityId: string,
  cycleId: string,
  propertyId: string
): Promise<Building[]> {
  try {
    return await db.buildings
      .where('[municipalityId+cycleId+propertyId]')
      .equals([municipalityId, cycleId, propertyId])
      .toArray();
  } catch (err) {
    throw new DatabaseError('Failed to get buildings for property', err);
  }
}

// ============================================================================
// Map layers + bundle manifests (inspection 1b-1)
// ============================================================================

export async function saveMapLayerRows(rows: MapLayerRow[]): Promise<void> {
  try {
    await db.transaction('rw', db.mapLayers, async () => {
      await db.mapLayers.bulkPut(rows);
    });
  } catch (err) {
    // Dexie's bulkPut rejects with a BulkError whose per-row failures live in
    // err.failures — a quota failure arrives NESTED, not as the top-level name.
    const failures = err instanceof Error && err.name === 'BulkError'
      ? Object.values((err as unknown as { failures?: Error[] | Record<number, Error> }).failures ?? {})
      : [];
    const isQuota = (err instanceof Error && err.name === 'QuotaExceededError')
      || failures.some(f => f?.name === 'QuotaExceededError');
    if (isQuota) {
      throw new StorageQuotaError();
    }
    throw new DatabaseError('Failed to save map layers', err);
  }
}

export async function getMapLayerRowsForMuni(municipalityId: string): Promise<MapLayerRow[]> {
  try {
    return await db.mapLayers.where('municipalityId').equals(municipalityId).toArray();
  } catch (err) {
    throw new DatabaseError('Failed to read map layers', err);
  }
}

export async function getMapLayerRowKeysForMuni(municipalityId: string): Promise<string[]> {
  try {
    // primaryKeys() is index-only — presence checks must never materialize
    // the geojson strings on plan-open (spec §7).
    const keys = await db.mapLayers.where('municipalityId').equals(municipalityId).primaryKeys();
    return keys as string[];
  } catch (err) {
    throw new DatabaseError('Failed to read map layer keys', err);
  }
}

export async function getMapLayerRow(id: string): Promise<MapLayerRow | undefined> {
  try {
    return await db.mapLayers.get(id);
  } catch (err) {
    throw new DatabaseError('Failed to read map layer', err);
  }
}

export async function deleteMapLayerRowsNotIn(municipalityId: string, keepIds: string[]): Promise<number> {
  try {
    const keep = new Set(keepIds);
    const ids = (await db.mapLayers.where('municipalityId').equals(municipalityId).primaryKeys())
      .filter(id => !keep.has(id as string));
    await db.mapLayers.bulkDelete(ids as string[]);
    return ids.length;
  } catch (err) {
    throw new DatabaseError('Failed to prune map layers', err);
  }
}

/**
 * Partial style/name refresh (inspection 1c-2) — the backend converts a
 * dropped SLD into a style v2 object WITHOUT bumping the registry doc's
 * `version`, so this must stay a cheap partial Dexie update, never a full
 * row replace that would require re-downloading the GeoJSON.
 */
export async function updateMapLayerRowMeta(
  id: string, meta: { style: Record<string, unknown>; name: string },
): Promise<void> {
  await db.mapLayers.update(id, meta);   // no-op (returns 0) when the row is absent
}

export async function getBundleManifest(id: string): Promise<BundleManifest | undefined> {
  try {
    return await db.bundleManifests.get(id);
  } catch (err) {
    throw new DatabaseError('Failed to read bundle manifest', err);
  }
}

/**
 * Delete EVERY bundle-manifest row for a municipality — vectors/ortho/
 * basemap/pdfs alike, across every planScope (Clear Data, fix 3: the field-
 * test's ONLY offline wipe tool on an installed iOS PWA left these rows
 * behind). A manifest is muni-scoped, never cycle-scoped (BundleManifest
 * carries no cycleId — a device only ever holds one active cycle's download
 * per muni at a time, see RecordCardMetaRow's doc comment for the same
 * assumption), so nothing narrower than "the whole muni" is meaningful here.
 */
export async function deleteBundleManifestsForMuni(municipalityId: string): Promise<number> {
  try {
    const ids = await db.bundleManifests.where('municipalityId').equals(municipalityId).primaryKeys();
    await db.bundleManifests.bulkDelete(ids as string[]);
    return ids.length;
  } catch (err) {
    throw new DatabaseError('Failed to delete bundle manifests', err);
  }
}

export async function putBundleManifest(manifest: BundleManifest): Promise<void> {
  try {
    await db.bundleManifests.put(manifest);
  } catch (err) {
    if (err instanceof Error && err.name === 'QuotaExceededError') {
      throw new StorageQuotaError();
    }
    throw new DatabaseError('Failed to save bundle manifest', err);
  }
}

/** The ortho bundle's manifest id for a municipality (single ortho bundle per
 *  muni, muni-scoped → UNPLANNED_PLAN_ID). Shared by the sync service, the
 *  depth picker (remembered choice), and use-ortho-layer (nativeMaxZoom). */
export function orthoManifestId(municipalityId: string): string {
  return `${municipalityId}_${UNPLANNED_PLAN_ID}_ortho`;
}

// ============================================================================
// Record cards (inspection 1d-1) — split meta/blob tables, see
// RecordCardMetaRow doc comment for why.
// ============================================================================

/**
 * Map a Dexie write rejection to the right error type for the record-card
 * stores. A quota failure can arrive either as a top-level QuotaExceededError
 * OR nested inside a bulkPut's BulkError (err.failures) — both mean the device
 * is out of space, so both surface as StorageQuotaError (spec §5: the write
 * failure IS the budget signal). Anything else wraps as DatabaseError. Shared
 * by saveRecordCards / saveRecordCardManifests / replaceRecordCardsWithMarkDrop
 * so the quota-detection stays in one place.
 */
function mapRecordCardWriteError(err: unknown, message: string): Error {
  const failures = err instanceof Error && err.name === 'BulkError'
    ? Object.values((err as unknown as { failures?: Error[] | Record<number, Error> }).failures ?? {})
    : [];
  const isQuota = (err instanceof Error && err.name === 'QuotaExceededError')
    || failures.some(f => f?.name === 'QuotaExceededError');
  if (isQuota) return new StorageQuotaError();
  return new DatabaseError(message, err);
}

/**
 * Merge each existing local row's durable `syncedPhotoEvidence` marker (see
 * RecordCardMetaRow's doc comment, B2 review finding [2]) onto its incoming
 * replacement BEFORE a bulkPut commits. bulkPut is whole-row `put` semantics
 * — the incoming meta objects saveRecordCards / replaceRecordCardsWithMarkDrop
 * receive are built fresh from a server payload/echo
 * (record-card-sync.service.ts downloadChunkOnce) that never sets this
 * device-local-only field, so a plain bulkPut on a card re-download or
 * version bump would silently wipe an already-stamped marker. `source` /
 * `registered` survive that same bulkPut only because
 * record-card-sync.service.ts re-derives them from data the server echo
 * already carries (item.source ?? doc.source; registered: 1) —
 * `syncedPhotoEvidence` has no server-side value to re-derive, so it must be
 * read back from the row already on device instead.
 *
 * Mutates `metas` in place and MUST run inside the same transaction as the
 * bulkPut it feeds, so the read-then-write is atomic against any
 * interleaving writer.
 *
 * Carried over ONLY when the existing row's `cycleId` matches the incoming
 * meta's `cycleId` (round-3 B2 re-review): a same-cycle re-download or
 * version bump is exactly the case this function exists to protect, but a
 * card re-download that lands in a NEW cycle must NOT drag the old cycle's
 * evidence flag forward — every other evidence signal in this file
 * (parcels' `syncedPhotoCount`, the `photos` table itself) is
 * [muni+cycle]-scoped, and a stale flag surviving a cycle rollover would
 * falsely mark an untouched-this-cycle roll as photographed: it would show
 * the Photos chip in the new cycle's unmapped-cards list, count as
 * completed, and Hide-completed would hide it from the inspector entirely.
 */
async function carryOverSyncedPhotoEvidence(metas: RecordCardMetaRow[]): Promise<void> {
  if (!metas.length) return;
  const existing = await db.recordCards.bulkGet(metas.map(m => m.id));
  for (let i = 0; i < metas.length; i++) {
    if (
      metas[i].syncedPhotoEvidence === undefined
      && existing[i]?.syncedPhotoEvidence === 1
      && existing[i]?.cycleId === metas[i].cycleId
    ) {
      metas[i] = { ...metas[i], syncedPhotoEvidence: 1 };
    }
  }
}

export async function saveRecordCards(
  metas: RecordCardMetaRow[],
  blobs: RecordCardBlobRow[],
  manifests: RecordCardManifestRow[] = [],
): Promise<void> {
  try {
    // recordCardManifests joins the transaction (finding [34c]): the field
    // manifest that rides a card download commits ATOMICALLY with its meta+blob
    // — closing the crash/quota window between the old two-transaction writes
    // that otherwise stranded a card without a manifest until its bytes changed
    // server-side. Manifests are best-effort, so an EMPTY array is the common
    // case (no manifest URL, or a negative row already folded in by the caller).
    await db.transaction('rw', db.recordCards, db.recordCardBlobs, db.recordCardManifests, async () => {
      // B2 review finding [2]: carry the durable syncedPhotoEvidence marker
      // over from each row's existing local copy before it is overwritten —
      // see carryOverSyncedPhotoEvidence's doc comment for why this bulkPut
      // would otherwise silently wipe it on a re-download/version bump.
      await carryOverSyncedPhotoEvidence(metas);
      await db.recordCards.bulkPut(metas);
      await db.recordCardBlobs.bulkPut(blobs);
      if (manifests.length) await db.recordCardManifests.bulkPut(manifests);
    });
  } catch (err) {
    // Dexie's bulkPut rejects with a BulkError whose per-row failures live in
    // err.failures — a quota failure arrives NESTED, not as the top-level name.
    throw mapRecordCardWriteError(err, 'Failed to save record cards');
  }
}

/**
 * Local mint of the cardless field sketch (design spec "Field trigger"): seed
 * the canonical blank one-page card straight into the recordCards/
 * recordCardBlobs tables, using the SAME row shapes saveRecordCards writes,
 * so a seeded blank flows through the entire existing card-open/annotation
 * path unchanged. Fully offline — no network call (server registration is a
 * separate, later step; see `registerFieldBlankCard` in
 * record-card-sync.service.ts).
 *
 * Idempotent: a roll that already has ANY local meta (a real card, a prior
 * blank, or one seeded a moment ago by a double-tap) is a no-op that returns
 * the EXISTING row untouched — a real/already-seeded card always wins here,
 * mirroring the backend mint endpoint's own idempotency rule ("if the roll
 * already has a card, do not overwrite"). The existence check and the write
 * run inside ONE `rw` transaction (rather than delegating to saveRecordCards,
 * which opens its own separate transaction) so a double-tap race can never
 * both observe "absent" and both write — the second writer's transaction
 * serializes behind the first and sees the just-created row.
 */
export async function seedFieldBlankCard(
  municipalityId: string,
  cycleId: string,
  roll: string,
  propertyId?: string,
): Promise<RecordCardMetaRow> {
  const id = `${municipalityId}_${roll}`;
  try {
    return await db.transaction('rw', db.recordCards, db.recordCardBlobs, async () => {
      const existing = await db.recordCards.get(id);
      if (existing) return existing;

      const bytes = blankCardPdfBytes();
      const meta: RecordCardMetaRow = {
        id,
        municipalityId,
        cycleId,
        roll,
        ...(propertyId ? { propertyId } : {}),
        version: 1,
        // Derived from the decoded canonical bytes (not a duplicated `518`
        // literal) so this can never drift from BLANK_CARD_PDF_B64 itself.
        fileBytes: bytes.byteLength,
        pageCount: 1,
        sha256: BLANK_CARD_SHA256,
        downloadedAt: Date.now(),
        source: FIELD_BLANK_SOURCE,
        registered: 0,
      };
      const blob: RecordCardBlobRow = { id, municipalityId, bytes: bytes.buffer as ArrayBuffer };
      await db.recordCards.put(meta);
      await db.recordCardBlobs.put(blob);
      return meta;
    });
  } catch (err) {
    throw mapRecordCardWriteError(err, 'Failed to seed field-blank card');
  }
}

export async function getRecordCardMetasForMuni(municipalityId: string): Promise<RecordCardMetaRow[]> {
  try {
    // Meta table only — NEVER touches recordCardBlobs. This is what makes
    // delta-sync version comparisons cheap (see RecordCardMetaRow doc comment).
    return await db.recordCards.where('municipalityId').equals(municipalityId).toArray();
  } catch (err) {
    throw new DatabaseError('Failed to read record card metas', err);
  }
}

/** In-place propertyId patch on version-matched card metas (voice-capture
 * fallback heal). Metas written by builds that predate propertyId in the
 * meta contract can never receive it through the delta — an unchanged
 * version is never re-downloaded — so the sync reconciles this meta-only,
 * listing-carried field directly. Blobs and manifests are untouched. */
export async function updateRecordCardMetaPropertyIds(
  updates: Array<{ id: string; propertyId: string | undefined }>,
): Promise<void> {
  try {
    await db.transaction('rw', db.recordCards, async () => {
      for (const u of updates) {
        await db.recordCards.update(u.id, { propertyId: u.propertyId });
      }
    });
  } catch (err) {
    throw mapRecordCardWriteError(err, 'Failed to update record card propertyIds');
  }
}

export async function getRecordCardMeta(id: string): Promise<RecordCardMetaRow | undefined> {
  try {
    return await db.recordCards.get(id);
  } catch (err) {
    throw new DatabaseError('Failed to read record card meta', err);
  }
}

export async function getRecordCardBlob(id: string): Promise<RecordCardBlobRow | undefined> {
  try {
    return await db.recordCardBlobs.get(id);
  } catch (err) {
    throw new DatabaseError('Failed to read record card blob', err);
  }
}

/**
 * Cheap existence probe across the three muni-scoped stores Clear Data (fix
 * 3) owns: recordCards, mapLayers, bundleManifests. Fix-wave-1 finding F1:
 * `FieldInspectionTab`'s offline-status `hasData` counted parcels ONLY, so a
 * device that had already run the OLD (parcels-only) Clear Data — parcels
 * wiped to zero, but gigabytes of record cards / map layers / bundle
 * manifests still resident — could never see the Clear Data button again,
 * stranding exactly the debris the fix exists to let a device clear. Each
 * probe is a pure index count (`.where('municipalityId').equals(...)`) — the
 * same index-only idiom as `getPendingSyncCountByCycle` above — so this stays
 * cheap enough to run on every tab load even for a muni with a large
 * resident card/layer set.
 */
export async function hasResidentCardOrLayerData(municipalityId: string): Promise<boolean> {
  try {
    const [cardCount, layerCount, manifestCount] = await Promise.all([
      db.recordCards.where('municipalityId').equals(municipalityId).count(),
      db.mapLayers.where('municipalityId').equals(municipalityId).count(),
      db.bundleManifests.where('municipalityId').equals(municipalityId).count(),
    ]);
    return cardCount > 0 || layerCount > 0 || manifestCount > 0;
  } catch (err) {
    throw new DatabaseError('Failed to probe resident card/layer data', err);
  }
}

/**
 * Prune every local record card NOT in `keepIds` (the server's enumeration),
 * WITH ONE exemption (cardless field sketch, Wave G Task B — design spec's
 * "one real race"): a `source === FIELD_BLANK_SOURCE` row that is not yet
 * confirmed `registered: 1` is exempt regardless of the server enumeration —
 * the server genuinely does not know about it yet (or, `registered: 2`,
 * never will — a terminal mint failure), so it can never legitimately appear
 * in `keepIds`. Without this, a background sync tick could wipe an in-
 * progress field sketch mid-drawing the instant it runs before the
 * fire-and-forget registration call lands. Once a blank IS registered
 * (`registered: 1`), the server enumerates it from here on and it is no
 * longer exempt — a server-side delete then prunes it exactly like any real
 * card (record-card-sync.service.ts's registration retry pass runs BEFORE
 * this prune each sync, so a roll registered earlier in the SAME run is
 * already reflected in that run's `keepIds`/enumeration too).
 *
 * This function has exactly two callers, whose intents differ (fix wave,
 * Wave G final-review finding [2]):
 *  - record-card-sync.service.ts's per-sync prune (`opts` omitted, default
 *    behavior): the exemption above MUST hold here — this is precisely the
 *    background race it exists to survive.
 *  - FieldInspectionTab's Clear Data (`{ includeFieldBlanks: true }`): an
 *    explicit, user-requested FULL wipe of a muni's offline store. This is
 *    safe to take everything, unregistered/terminal blanks included — the
 *    seed is idempotent locally and the mint is idempotent server-side, so
 *    re-tapping Draw after a clear fully restores the sketch, and cardMarks
 *    (roll-keyed, deliberately preserved by the wipe — see that handler) are
 *    untouched either way. Leaving the exemption on for this caller would
 *    strand exempt rows forever: `hasResidentCardOrLayerData` would keep
 *    reporting true and repeated clears would no-op on exactly the rows the
 *    user asked to wipe.
 *
 * This reads full rows (`.each`), not just `.primaryKeys()` as before,
 * because the exemption needs `source`/`registered` off each row. Record-card
 * META rows are KB-scale JSON (no ArrayBuffer) — not the blob/manifest OOM
 * class the meta/blob split exists to avoid — so this stays cheap even for a
 * muni with thousands of cards.
 */
export async function deleteRecordCardsNotIn(
  municipalityId: string,
  keepIds: string[],
  opts?: { includeFieldBlanks?: boolean },
): Promise<number> {
  try {
    const includeFieldBlanks = opts?.includeFieldBlanks ?? false;
    const keep = new Set(keepIds);
    const ids: string[] = [];
    await db.recordCards.where('municipalityId').equals(municipalityId).each(row => {
      if (keep.has(row.id)) return;
      if (!includeFieldBlanks && row.source === FIELD_BLANK_SOURCE && row.registered !== 1) return; // prune-exempt
      ids.push(row.id);
    });
    await db.transaction('rw', db.recordCards, db.recordCardBlobs, db.recordCardManifests, async () => {
      await db.recordCards.bulkDelete(ids);
      await db.recordCardBlobs.bulkDelete(ids);
      // Prune orphaned manifests in lockstep with metas/blobs (inspection 2c).
      await db.recordCardManifests.bulkDelete(ids);
    });
    return ids.length;
  } catch (err) {
    throw new DatabaseError('Failed to prune record cards', err);
  }
}

/**
 * Delete ONE record card's meta+blob+manifest rows by id (cardless field
 * sketch, Wave G Task B: the "real card always wins" cleanup — a mint
 * response with `alreadyExists: true` and a non-field-blank `source` means a
 * REAL card exists server-side, so the local blank is stale and dropped
 * outright). Distinct from `deleteRecordCardsNotIn` (complement-of-a-keep-set
 * prune): this targets ONE specific id the caller has already decided is
 * stale, so it needs no keepIds computation. `cardMarks` are DELIBERATELY not
 * touched — they are roll-keyed (not card-id-keyed) and survive a blank ->
 * real supersede exactly like any other real-card version bump (design spec:
 * "the synced sketch leaves the device but remains visible office-side").
 * A missing id is a silent no-op (Dexie's delete on an absent key never
 * throws) — safe to call from an idempotent registration retry.
 */
export async function deleteRecordCardById(id: string): Promise<void> {
  try {
    await db.transaction('rw', db.recordCards, db.recordCardBlobs, db.recordCardManifests, async () => {
      await db.recordCards.delete(id);
      await db.recordCardBlobs.delete(id);
      await db.recordCardManifests.delete(id);
    });
  } catch (err) {
    throw new DatabaseError('Failed to delete record card', err);
  }
}

/**
 * Patch ONLY the `registered` bookkeeping flag on a record-card meta row
 * (cardless field sketch, Wave G Task B — see RecordCardMetaRow's doc
 * comment for the 0/1/2 states). Partial update, same precedent as
 * `updateRecordCardMetaPropertyIds` — blob/manifest untouched. A missing id
 * is a silent no-op (Dexie's `.update()` returns 0, never throws) — safe to
 * call from an idempotent registration retry against a roll whose local meta
 * was since deleted (e.g. a real-card supersede that raced this call).
 */
export async function updateRecordCardRegistration(id: string, registered: 0 | 1 | 2): Promise<void> {
  try {
    await db.recordCards.update(id, { registered });
  } catch (err) {
    throw new DatabaseError('Failed to update record card registration', err);
  }
}

/**
 * Atomic replace-and-drop for a version bump (Task 14, spec B6). bulkPuts the
 * new metas+blobs (+manifests), then for each drop deletes the roll's SYNCED
 * marks whose `cardVersion` predates the served version — all in ONE `rw`
 * transaction spanning recordCards, recordCardBlobs, recordCardManifests AND
 * cardMarks. (Unsynced old-version marks survive — see the in-loop
 * adjudication comment.)
 *
 * WHY one transaction: the marks being dropped are the OLD version's marks that
 * have ALREADY reached the server — the sync-before-replace gate only lets a
 * roll through once its unsynced count is 0 (record-card-sync.service.ts). So
 * replacing the card bytes and dropping those now-orphaned local marks must be
 * all-or-nothing: a crash in between would either strand marks pointing at a
 * card version the device no longer holds, or replace the card while marks the
 * office has not yet seen still sit local. This extends saveRecordCards' meta/
 * blob/manifest atomic write (finding [34c]) with cardMarks + the version drop.
 *
 * `cardVersion` is deliberately unindexed (2a schema note): the roll's marks are
 * index-selected via [municipalityId+cycleId+roll] and the version compare is a
 * JS filter over that small per-roll set (marks are KB-scale JSON). Deleting via
 * primaryKeys()+bulkDelete (not a Collection.delete()) keeps the drop a distinct,
 * spy-able write so a forced failure exercises the transaction rollback. Same
 * quota-error wrapping as saveRecordCards.
 *
 * B2 review finding [2]: like saveRecordCards, this bulkPuts metas built
 * fresh from a server payload — see carryOverSyncedPhotoEvidence's doc
 * comment for why that would otherwise silently wipe a roll's durable
 * syncedPhotoEvidence marker on a version bump, exactly the case this
 * function exists to handle.
 */
export async function replaceRecordCardsWithMarkDrop(
  metas: RecordCardMetaRow[],
  blobs: RecordCardBlobRow[],
  drops: Array<{ municipalityId: string; cycleId: string; roll: string; belowVersion: number }>,
  manifests: RecordCardManifestRow[] = [],
): Promise<void> {
  try {
    await db.transaction('rw', db.recordCards, db.recordCardBlobs, db.recordCardManifests, db.cardMarks, async () => {
      await carryOverSyncedPhotoEvidence(metas);
      await db.recordCards.bulkPut(metas);
      await db.recordCardBlobs.bulkPut(blobs);
      if (manifests.length) await db.recordCardManifests.bulkPut(manifests);
      for (const drop of drops) {
        // The `syncedFlag === 1` guard deviates from the plan's bare
        // `cardVersion < belowVersion` drop filter per the 2026-07-11
        // adjudication: a mark drawn in the gate-check→replace-commit window
        // (still unsynced) SURVIVES the replace and syncs on the next tick —
        // preserving it at the cost of a possible temporary local misrender
        // until the roll's next bump, instead of silent loss.
        const ids = await db.cardMarks
          .where('[municipalityId+cycleId+roll]')
          .equals([drop.municipalityId, drop.cycleId, drop.roll])
          .filter(m => m.cardVersion < drop.belowVersion && m.syncedFlag === 1)
          .primaryKeys();
        if (ids.length) await db.cardMarks.bulkDelete(ids as string[]);
      }
    });
  } catch (err) {
    throw mapRecordCardWriteError(err, 'Failed to replace record cards');
  }
}

// ============================================================================
// Record-card field manifests (inspection 2c) — see RecordCardManifestRow
// doc comment for why this is a separate store, not props on the meta row.
// ============================================================================

/**
 * Cache field manifests fetched alongside a card download batch. Best-effort
 * from the caller's perspective (record-card-sync.service.ts never fails a
 * card download over a missing/failed manifest fetch), but writes here use
 * the same quota-handling shape as saveRecordCards since a manifest write can
 * still legitimately exhaust device storage.
 */
export async function saveRecordCardManifests(rows: RecordCardManifestRow[]): Promise<void> {
  if (!rows.length) return;
  try {
    await db.recordCardManifests.bulkPut(rows);
  } catch (err) {
    // Dexie's bulkPut rejects with a BulkError whose per-row failures live in
    // err.failures — a quota failure arrives NESTED, not as the top-level name.
    throw mapRecordCardWriteError(err, 'Failed to save record card manifests');
  }
}

/** Shallow presence/version pointer for one cached manifest row — everything
 * the repair sweep needs, WITHOUT the 10-30KB manifest body. */
export interface RecordCardManifestPointer {
  id: string;
  cycleId: string;
  manifestVersion: number;
}

/**
 * Bulk-read shallow POINTERS for a muni's cached manifest rows — one Dexie
 * cursor scan the manifest repair sweep uses to find cards whose bytes are
 * present-and-current but whose field manifest never landed (finding [34b]).
 * Cursor iteration + per-row projection on purpose: `.toArray()` would hold
 * every manifest body in memory at once (thousands of cards × 10-30KB bodies
 * ≈ a 30-150MB transient allocation — the OOM class the meta/blob split doc
 * warns about). Each row still deserializes as the cursor visits it
 * (manifestVersion/cycleId aren't indexed, and no schema change is allowed),
 * but peak retained memory drops to ONE body plus the shallow pointer list.
 */
export async function getRecordCardManifestPointersForMuni(
  municipalityId: string,
): Promise<RecordCardManifestPointer[]> {
  try {
    const pointers: RecordCardManifestPointer[] = [];
    await db.recordCardManifests.where('municipalityId').equals(municipalityId).each(r => {
      pointers.push({ id: r.id, cycleId: r.cycleId, manifestVersion: r.manifestVersion });
    });
    return pointers;
  } catch (err) {
    throw new DatabaseError('Failed to read record card manifests', err);
  }
}

/**
 * Version-guarded manifest read for capture-time field anchoring. Returns
 * null for an absent row, a cycle mismatch (rollover), or a stale manifest
 * (cached against an older card version than the one currently on device) —
 * callers treat null as "no manifest available", never as an error.
 */
export async function getCardManifest(
  municipalityId: string,
  cycleId: string,
  roll: string,
  cardVersion: number,
): Promise<FieldBox[] | null> {
  try {
    const row = await db.recordCardManifests.get(`${municipalityId}_${roll}`);
    if (!row || row.cycleId !== cycleId || row.manifestVersion !== cardVersion) {
      return null;
    }
    return row.manifest;
  } catch (err) {
    throw new DatabaseError('Failed to read card manifest', err);
  }
}

// ============================================================================
// Card marks (inspection 2a) — local-only pencil/voice/arrow annotations on
// record-card pages, see CardMarkRow doc comment for scope.
// ============================================================================

/**
 * Persist one card mark (inspection 2a, spec §5). Persistence is optimistic
 * PER ACTION — called on every stroke/arrow/dot commit, so a failed put loses
 * only that mark and the sheet's five exit paths need no flush-on-close.
 */
export async function saveCardMark(mark: CardMarkRow): Promise<void> {
  try {
    await db.cardMarks.put(mark);
  } catch (err) {
    if (err instanceof Error && err.name === 'QuotaExceededError') {
      throw new StorageQuotaError();
    }
    throw new DatabaseError('Failed to save card mark', err);
  }
}

/**
 * Erase a mark (erase tool / undo). Rows the server has never seen (no
 * `syncedAt` AND no `uploadedAt`) hard-delete. Any row the server MAY have a
 * copy of — flipped `syncedAt`, or `uploadedAt` stamped by a CAS-skip replay
 * in markCardMarkSynced (first-sync-then-edit race) — becomes a TOMBSTONE
 * (re-pended for sync) so the next batch upload propagates the erase instead
 * of orphaning the server copy; markCardMarkSynced hard-deletes it once the
 * server confirms. Missing ids resolve — erase/undo double-fire safe.
 */
export async function deleteCardMark(id: string): Promise<void> {
  try {
    // Finding [02]: the get→delete/update runs inside ONE readwrite transaction
    // so an erase can never hard-delete based on a read that predates a
    // concurrent markCardMarkSynced stamping uploadedAt (the confirm-racing-erase
    // TOCTOU). Without the transaction, `get` here and the confirm's uploadedAt
    // update are separate Dexie auto-transactions and can interleave.
    await db.transaction('rw', db.cardMarks, async () => {
      const row = await db.cardMarks.get(id);
      if (!row) return;
      if (!row.syncedAt && !row.uploadedAt) {
        await db.cardMarks.delete(id);
        return;
      }
      const now = Date.now();
      await db.cardMarks.update(id, {
        deleted: true,
        deletedAt: now,
        updatedAt: now,
        synced: false,
        syncedFlag: 0,
      });
    });
  } catch (err) {
    throw new DatabaseError('Failed to delete card mark', err);
  }
}

/**
 * All marks for one card in one cycle (entity-isolated), oldest first so
 * render order matches creation order — hit-testing treats the LAST mark as
 * topmost (ink-engine hitMark).
 */
export async function getCardMarksForCard(
  municipalityId: string,
  cycleId: string,
  roll: string
): Promise<CardMarkRow[]> {
  try {
    const rows = await db.cardMarks
      .where('[municipalityId+cycleId+roll]')
      .equals([municipalityId, cycleId, roll])
      .filter((m) => !m.deleted)
      .toArray();
    return rows.sort((a, b) => a.createdAt - b.createdAt);
  } catch (err) {
    throw new DatabaseError('Failed to get card marks', err);
  }
}

/**
 * ≥1 live (non-deleted) card mark for the roll, OR ≥1 local voice note
 * keyed by the property id — OR by the roll itself: the page-level voice
 * recorder falls back to `selectedParcel.propertyId || selectedParcel.id`
 * (FieldInspectionPage voiceRecorder config), so legacy/parcel-without-
 * propertyId rows key voiceNotes by roll (fieldmap 4-state spec §4.3).
 *
 * voiceNotes are counted INDEX-ONLY (`.primaryKeys()` over
 * [municipalityId+cycleId+propertyId]): voiceNote rows embed ArrayBuffer
 * audio bytes and must never be deserialized for counting. cardMarks rows
 * are KB-scale JSON — safe to materialize. VoiceNote has no tombstone
 * concept (deletes are hard db.voiceNotes.delete) — no deleted filter on
 * the voice side; local voice notes are NOT cleared after sync (voice-sync
 * has no clearSynced pass, unlike photos), so synced local notes still count.
 *
 * Dead-flagged voice notes (Kurt item 8 residual, spec §C4 — bytes
 * permanently gone or a permanent 400) are subtracted via a second tiny
 * index-only `.primaryKeys()` read over the standalone `deadFlag` index, kept
 * index-only the same way.
 */
export async function parcelHasLocalNotes(
  municipalityId: string,
  cycleId: string,
  roll: string,
  propertyId?: string,
): Promise<boolean> {
  try {
    const marks = await db.cardMarks
      .where('[municipalityId+cycleId+roll]')
      .equals([municipalityId, cycleId, roll])
      .toArray();
    if (marks.some(m => !m.deleted)) return true;
    const keys: Array<[string, string, string]> = [[municipalityId, cycleId, roll]];
    if (propertyId && propertyId !== roll) {
      keys.push([municipalityId, cycleId, propertyId]);
    }
    const voiceIds = (await db.voiceNotes
      .where('[municipalityId+cycleId+propertyId]')
      .anyOf(keys)
      .primaryKeys()) as string[];
    if (voiceIds.length === 0) return false;
    const deadIds = (await db.voiceNotes.where('deadFlag').equals(1).primaryKeys()) as string[];
    if (deadIds.length === 0) return true;
    const deadSet = new Set(deadIds);
    return voiceIds.some(id => !deadSet.has(id));
  } catch (err) {
    throw new DatabaseError('Failed to check parcel note evidence', err);
  }
}

/** Cycle-wide sweep signals for load-time reconciliation (Feature 2 §4.3,
 *  extended by Feature 7 §7.3 with MAX evidence timestamps so the sweep can
 *  apply per-parcel round cutoffs — `maxTime > cutoff` ⇔ "≥1 qualifying
 *  note exists"). The two Set fields are kept (= the maps' key sets).
 *
 *  voiceNotes reads are STRICTLY index-only — rows embed multi-MB audio
 *  ArrayBuffers that must never be deserialized for counting. keys() and
 *  primaryKeys() on the same query are aligned key-cursor walks over the
 *  same index order, so zipping them yields (indexKey, id) pairs without
 *  touching values; the timestamp index walk joins id -> timestamp the
 *  same way. cardMarks rows are KB-scale JSON — safe to `.each`.
 *
 *  Dead-flagged voice notes (Kurt item 8 residual, spec §C4) are excluded
 *  from both the id set and the MAX-timestamp computation via one more
 *  index-only `.primaryKeys()` read over the standalone `deadFlag` index
 *  (a tiny set — most notes are never dead), checked by id membership as
 *  the scoped rows are zipped below. */
export async function getLocalNoteSignals(
  municipalityId: string,
  cycleId: string,
): Promise<{
  noteRolls: Set<string>;
  voicePropertyIds: Set<string>;
  noteTimesByRoll: Map<string, number>;
  voiceTimesByPropertyId: Map<string, number>;
}> {
  try {
    const noteTimesByRoll = new Map<string, number>();
    await db.cardMarks
      .where('[municipalityId+cycleId+roll]')
      .between([municipalityId, cycleId, Dexie.minKey], [municipalityId, cycleId, Dexie.maxKey])
      .each(m => {
        if (m.deleted) return;
        const prev = noteTimesByRoll.get(m.roll) ?? 0;
        if (m.createdAt > prev) noteTimesByRoll.set(m.roll, m.createdAt);
      });

    const deadIds = new Set(
      (await db.voiceNotes.where('deadFlag').equals(1).primaryKeys()) as string[],
    );

    const scopedKeys = (await db.voiceNotes
      .where('[municipalityId+cycleId+propertyId]')
      .between([municipalityId, cycleId, Dexie.minKey], [municipalityId, cycleId, Dexie.maxKey])
      .keys()) as unknown as Array<[string, string, string]>;
    const scopedIds = (await db.voiceNotes
      .where('[municipalityId+cycleId+propertyId]')
      .between([municipalityId, cycleId, Dexie.minKey], [municipalityId, cycleId, Dexie.maxKey])
      .primaryKeys()) as string[];
    const timeKeys = (await db.voiceNotes.orderBy('timestamp').keys()) as unknown as number[];
    const timeIds = (await db.voiceNotes.orderBy('timestamp').primaryKeys()) as string[];
    const timeById = new Map<string, number>();
    timeKeys.forEach((t, i) => timeById.set(timeIds[i], Number(t)));

    const voiceTimesByPropertyId = new Map<string, number>();
    scopedKeys.forEach((key, i) => {
      if (deadIds.has(scopedIds[i])) return;
      const propertyId = key[2];
      // saveVoiceNote always stamps `timestamp`; a hypothetical row without
      // one is absent from the timestamp index -> treated as ms 0 (pre-round
      // under any cutoff — mirrors the backend's missing-created_at rule).
      const time = timeById.get(scopedIds[i]) ?? 0;
      const prev = voiceTimesByPropertyId.get(propertyId);
      if (prev === undefined || time > prev) voiceTimesByPropertyId.set(propertyId, time);
    });

    return {
      noteRolls: new Set(noteTimesByRoll.keys()),
      voicePropertyIds: new Set(voiceTimesByPropertyId.keys()),
      noteTimesByRoll,
      voiceTimesByPropertyId,
    };
  } catch (err) {
    throw new DatabaseError('Failed to get local note signals', err);
  }
}

/**
 * Replace an arrow mark's handwriting strokes (called on each note-stroke
 * pointerup — optimistic per action, spec §5).
 */
export async function updateCardMarkNoteStrokes(
  id: string,
  noteStrokes: Array<Array<{ x: number; y: number }>>
): Promise<void> {
  let updated = 0;
  try {
    updated = await db.cardMarks.update(id, {
      noteStrokes,
      updatedAt: Date.now(),
      synced: false,
      syncedFlag: 0,
    });
  } catch (err) {
    if (err instanceof Error && err.name === 'QuotaExceededError') {
      throw new StorageQuotaError();
    }
    throw new DatabaseError('Failed to update card mark note strokes', err);
  }
  if (!updated) {
    throw new DatabaseError(`Card mark not found: ${id}`);
  }
}

/**
 * Move an arrow or location mark's note bubble: update its `tail` only —
 * the arrow's `point` (tip) and a location's latitude/longitude are left
 * untouched, so a moved bubble still points at the same field/geo point.
 */
export async function updateCardMarkTail(
  id: string,
  tail: { x: number; y: number }
): Promise<void> {
  let updated = 0;
  try {
    updated = await db.cardMarks.update(id, {
      tail,
      updatedAt: Date.now(),
      synced: false,
      syncedFlag: 0,
    });
  } catch (err) {
    if (err instanceof Error && err.name === 'QuotaExceededError') {
      throw new StorageQuotaError();
    }
    throw new DatabaseError('Failed to update card mark tail', err);
  }
  if (!updated) {
    throw new DatabaseError(`Card mark not found: ${id}`);
  }
}

/**
 * Resize an arrow mark's note bubble (drag-corner resize grip, note-bubble
 * Task 4). Only the FRAME (`bubbleW`/`bubbleH`) changes — `noteStrokes` are
 * bubble-local CSS px and are never rescaled, so no coordinate migration is
 * needed. Absent ⇒ legacy default 210×52 (see CardMarkRow).
 */
export async function updateCardMarkBubbleSize(
  id: string,
  bubbleW: number,
  bubbleH: number
): Promise<void> {
  let updated = 0;
  try {
    updated = await db.cardMarks.update(id, {
      bubbleW, bubbleH, updatedAt: Date.now(), synced: false, syncedFlag: 0,
    });
  } catch (err) {
    if (err instanceof Error && err.name === 'QuotaExceededError') throw new StorageQuotaError();
    throw new DatabaseError('Failed to update card mark bubble size', err);
  }
  if (!updated) throw new DatabaseError(`Card mark not found: ${id}`);
}

/**
 * Persist the 2c confirm/dismiss chip decision (finding [12]). Confirm passes
 * `{ fieldConfirmed: true }`; dismiss passes the four field-resolution keys as
 * `undefined` (Dexie 4 DELETES a key whose update value is `undefined` —
 * verified), so a wrong anchor is fully cleared. Uses the same partial
 * `db.cardMarks.update` precedent as updateCardMarkNoteStrokes rather than a
 * full-row `saveCardMark` put: the sheet's in-memory copy drifts stale once
 * background auto-sync stamps syncedAt/uploadedAt, and round-tripping that stale
 * row through a full put would WIPE syncedAt/uploadedAt — after which a later
 * erase hard-deletes instead of tombstoning and orphans the server copy. The
 * partial update preserves syncedAt/uploadedAt while re-pending the row
 * (synced:false, syncedFlag:0) so the confirm/dismiss itself uploads.
 */
export async function updateCardMarkFieldResolution(
  id: string,
  patch: Partial<Pick<CardMarkRow, 'resolvedField' | 'fieldLabel' | 'fieldConfidence' | 'fieldConfirmed'>>,
): Promise<void> {
  let updated = 0;
  try {
    updated = await db.cardMarks.update(id, {
      ...patch,
      updatedAt: Date.now(),
      synced: false,
      syncedFlag: 0,
    });
  } catch (err) {
    if (err instanceof Error && err.name === 'QuotaExceededError') throw new StorageQuotaError();
    throw new DatabaseError('Failed to update card mark field resolution', err);
  }
  if (!updated) throw new DatabaseError(`Card mark not found: ${id}`);
}

/**
 * Flip a mark to synced after a successful batch upload. Tombstones are
 * hard-deleted here — once the server has recorded the delete, the local
 * row's only job (propagating the erase) is done. `synced`/`syncedFlag`
 * flip in lockstep (photos-v9 lesson). Missing ids resolve.
 *
 * `uploaded` is an optional CAS (compare-and-swap) snapshot — the exact row
 * that was serialized into the batch payload for this confirm. Passing it
 * closes two TOCTOU races between the upload snapshot and this call
 * (T4 review findings):
 *  - edit-during-sync clobber: if the live row's `updatedAt` no longer
 *    matches `uploaded.updatedAt`, the user changed the row (e.g. new note
 *    strokes) while the upload was in flight. Resolve WITHOUT flipping —
 *    the row stays pending (`synced: false`) and re-uploads next pass, so
 *    the newer edit is never silently stamped over by a stale `synced: true`.
 *    It DOES stamp `uploadedAt` — the server accepted a copy of this id, so
 *    a later `deleteCardMark` must tombstone (propagate the erase) instead
 *    of hard-deleting and orphaning that server copy.
 *  - erase-during-sync: if the row is missing (hard-deleted by
 *    `deleteCardMark` because it had never synced) but `uploaded` shows a
 *    live, non-tombstone mark, the server accepted a copy the local erase
 *    raced past. Resurrect a tombstone — `deleted: true` with `syncedAt`
 *    already set (so the NEXT confirm hard-deletes it instead of treating
 *    it as a fresh, never-synced tombstone) — so the erase still propagates
 *    to the server on the next sync pass.
 * Callers that omit `uploaded` keep the original unconditional-flip
 * behavior (back-compat for existing 1-arg call sites).
 */
export async function markCardMarkSynced(id: string, uploaded?: CardMarkRow): Promise<void> {
  try {
    // Finding [02]: the get→mutate runs inside ONE readwrite transaction so a
    // concurrent deleteCardMark can never observe a torn state (e.g. read the
    // row before this stamps uploadedAt, then hard-delete a server-held copy).
    // `db.recordCards` is in the transaction scope (read-only here) so the
    // resurrect decision and the meta-version read it depends on are one atomic
    // unit — NOT a schema change (Dexie stays v13; scope ≠ schema).
    await db.transaction('rw', db.cardMarks, db.recordCards, async () => {
      const row = await db.cardMarks.get(id);
      if (!row) {
        if (uploaded && !uploaded.deleted) {
          // I-2: a missing row has TWO causes now. The resurrect premise is
          // "erase raced the first-ever sync" — but Task 14's version-bump
          // replace-drop also deletes SYNCED old-version marks. If the roll's
          // card on device is a NEWER version than the mark we're confirming,
          // this row was version-DROPPED, not user-erased; resurrecting a
          // tombstone would fabricate an erase the server's LWW would apply to
          // a live synced mark (silent office-side data loss). Skip it. The
          // residual true-erase-corner cost of skipping (an orphaned server
          // copy) is the accepted Wave-5 class and is recoverable.
          const meta = await db.recordCards.get(`${uploaded.municipalityId}_${uploaded.roll}`);
          if (meta && meta.version > uploaded.cardVersion) {
            return;
          }
          const now = Date.now();
          await db.cardMarks.put({
            ...uploaded,
            deleted: true,
            deletedAt: now,
            updatedAt: now,
            synced: false,
            syncedFlag: 0,
            syncedAt: now,
            uploadedAt: now,
          });
        }
        return;
      }
      if (uploaded && row.updatedAt !== uploaded.updatedAt) {
        await db.cardMarks.update(id, { uploadedAt: Date.now() });
        return;
      }
      if (row.deleted) {
        await db.cardMarks.delete(id);
        return;
      }
      const syncedNow = Date.now();
      await db.cardMarks.update(id, {
        synced: true,
        syncedFlag: 1,
        syncedAt: syncedNow,
        uploadedAt: syncedNow,
      });
    });
  } catch (err) {
    throw new DatabaseError('Failed to mark card mark synced', err);
  }
}

/**
 * Stamp `uploadedAt` on a batch of marks at POST-ATTEMPT time — BEFORE the
 * network call (finding [02]). If the response is lost after the backend has
 * already committed the upsert (connection drop / Safari kills the PWA between
 * the POST resolving and the per-mark bookkeeping loop), the row would otherwise
 * carry no syncedAt and no uploadedAt, and a later erase would HARD-DELETE it —
 * orphaning the server copy forever (no device can ever re-tombstone it). The
 * pre-POST stamp flips a subsequent erase to a tombstone instead, so the delete
 * still propagates. Deliberately touches ONLY uploadedAt: it must NOT bump
 * `updatedAt` (markCardMarkSynced's CAS keys on it — a bump would drop a real
 * mid-flight edit) and must NOT flip `syncedFlag` (the row is not confirmed
 * synced — the response has not arrived). A false-positive stamp (POST never
 * reached the server) is harmless: the batch endpoint upserts a deleted:true doc
 * for the unseen id and the next confirm hard-deletes the local tombstone — one
 * extra round trip, correct outcome.
 */
export async function markCardMarksUploadAttempted(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  try {
    await db.cardMarks.where('id').anyOf(ids).modify({ uploadedAt: Date.now() });
  } catch (err) {
    throw new DatabaseError('Failed to stamp card mark upload attempt', err);
  }
}

/**
 * Unsynced marks (tombstones included) for one cycle, oldest first so the
 * batch upload replays in creation order. Pure index select via
 * [municipalityId+cycleId+syncedFlag].
 */
export async function getUnsyncedCardMarksByCycle(
  municipalityId: string,
  cycleId: string
): Promise<CardMarkRow[]> {
  try {
    const rows = await db.cardMarks
      .where('[municipalityId+cycleId+syncedFlag]')
      .equals([municipalityId, cycleId, 0])
      .toArray();
    return rows.sort((a, b) => a.createdAt - b.createdAt);
  } catch (err) {
    throw new DatabaseError('Failed to get unsynced card marks', err);
  }
}

/**
 * Count a single roll's UNSYNCED card marks (Task 14 sync-before-replace gate,
 * spec B6). Tombstones (deleted:true) are rows with syncedFlag 0 too, so they
 * COUNT — a pending erase must gate a version-bump replace exactly like a
 * pending draw, or the replace would drop a delete the server has never seen.
 * Index-selects the roll via [municipalityId+cycleId+roll] then JS-filters
 * syncedFlag === 0 (the [municipalityId+cycleId+syncedFlag] index cannot also
 * pin the roll, so the per-roll count uses the roll index + a small JS filter).
 */
export async function getUnsyncedCardMarkCountForRoll(
  municipalityId: string,
  cycleId: string,
  roll: string,
): Promise<number> {
  try {
    return await db.cardMarks
      .where('[municipalityId+cycleId+roll]')
      .equals([municipalityId, cycleId, roll])
      .filter(m => m.syncedFlag === 0)
      .count();
  } catch (err) {
    throw new DatabaseError('Failed to count unsynced card marks for roll', err);
  }
}

/**
 * Pending card-mark count for the SyncStatus banner / auto-sync pre-check.
 * Pure index count — this runs on the 5s hot path and must not deserialize rows.
 */
export async function getPendingCardMarkSyncCount(
  municipalityId: string,
  cycleId: string
): Promise<number> {
  try {
    return await db.cardMarks
      .where('[municipalityId+cycleId+syncedFlag]')
      .equals([municipalityId, cycleId, 0])
      .count();
  } catch (err) {
    throw new DatabaseError('Failed to get pending card mark sync count', err);
  }
}

/**
 * Distinct (roll, page) pairs with unsynced card-mark rows — the SyncStatus
 * banner's page-unit chip (item 7). Tombstones COUNT: an erase is a change
 * waiting to sync. count() fast-path keeps the steady-state (0 pending)
 * 5s poll free; the toArray only runs while something is actually queued.
 */
export async function getPendingCardPageSyncCount(
  municipalityId: string,
  cycleId: string
): Promise<number> {
  try {
    const pending = db.cardMarks
      .where('[municipalityId+cycleId+syncedFlag]')
      .equals([municipalityId, cycleId, 0]);
    if ((await pending.count()) === 0) return 0;
    const rows = await pending.toArray();
    return new Set(rows.map((r) => `${r.roll}::${r.page}`)).size;
  } catch (err) {
    throw new DatabaseError('Failed to get pending card page sync count', err);
  }
}

/**
 * Cascade for erasing a voice dot: delete the linked voice note ONLY while
 * it is still unsynced. A synced note stays — it is already a property voice
 * note on the server, and there is no voice-note tombstone machinery.
 */
export async function deleteUnsyncedVoiceNote(noteId: string): Promise<void> {
  try {
    const note = await db.voiceNotes.get(noteId);
    if (!note || note.synced) return;
    await db.voiceNotes.delete(noteId);
  } catch (err) {
    throw new DatabaseError('Failed to cascade-delete voice note', err);
  }
}

